/* assets/littleg-kid-map.js — LITTLEG-FRIEND-MAP-RUNTIME-23
 * Product-specific interaction owner for /pages/kid-map.
 *
 * NINTENDO UX & FOUR-LAYER ACCESSIBILITY CONTRACT:
 * 1. Layer 1 (Focus & Keyboard): Single IIFE scope, Focus Trap & 100% Focus Restoration on modal close.
 * 2. Layer 2 (Touch & Target): 48px+ Chunky Touch Targets, anti-frustration danger button arming.
 * 3. Layer 3 (Contrast & Motion): Accessible ARIA status announcements, prefers-reduced-motion support.
 * 4. Layer 4 (Mapbox & Dialogs): Native <dialog> modal stack with Priority Escape dismissal.
 */
(function (window, document) {
  'use strict';

  var ROOT = '[data-littleg-kid-map]';
  var STAMP_KEY = 'littleg_kid_map_stamps_v1';
  var SAVE_KEY = 'littleg_kid_map_saved_v1';
  var MAX_LOCAL_ITEMS = 60;
  var MAX_STATION_RESULTS = 12;
  var VERSION = '2.18.8';
  var stationDatasetPromise = null;
  var stationIndex = [];
  var lastActiveFocusElement = null;

  function q(root, selector) {
    return root ? root.querySelector(selector) : null;
  }
  function qa(root, selector) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(selector)) : [];
  }
  function text(node) {
    return node ? String(node.textContent || '').trim() : '';
  }

  function clean(value, max) {
    return String(value == null ? '' : value)
      .replace(/[\u0000-\u001f\u007f<>"'`]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, max || 240);
  }

  function finite(value) {
    if (value == null || value === '') return null;
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function validCoordinates(lat, lng) {
    return lat !== null && lng !== null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  function safeParse(value, fallback) {
    try {
      var parsed = JSON.parse(value);
      return parsed == null ? fallback : parsed;
    } catch (_) {
      return fallback;
    }
  }

  function readLocal(key) {
    try {
      var value = safeParse(window.localStorage.getItem(key), []);
      return Array.isArray(value) ? value.slice(0, MAX_LOCAL_ITEMS) : [];
    } catch (_) {
      return [];
    }
  }

  function writeLocal(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value.slice(0, MAX_LOCAL_ITEMS)));
      return true;
    } catch (_) {
      return false;
    }
  }

  function isChinese() {
    return /^zh\b/i.test(document.documentElement.lang || '');
  }
  function uid(prefix) {
    return prefix + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
  }

  function normalizeSearchValue(value) {
    return clean(value, 160)
      .toLocaleLowerCase()
      .replace(/[\s_-]+/g, ' ')
      .trim();
  }

  function socialGraph() {
    var familyData = window.LittleGFamilyData;
    var graph = familyData && familyData.socialGraph;

    return graph &&
      typeof graph.listPlaces === 'function' &&
      typeof graph.getPlace === 'function' &&
      typeof graph.upsertPlace === 'function' &&
      typeof graph.removePlace === 'function'
      ? graph
      : null;
  }

  function confirmed(row) {
    return (
      !!row &&
      row.privacyConfirmed === true &&
      row.parentConfirmed === true &&
      row.approvalStatus === 'approved'
    );
  }

  function normalizeSocialRow(row) {
    if (!row || typeof row !== 'object') return null;

    var type =
      row.type === 'mine' || row.type === 'friend'
        ? row.type
        : row.kind === 'mine'
          ? 'mine'
          : 'friend';

    var locationType =
      clean(row.locationType || row.location_type, 80) === 'station_anchor'
        ? 'station_anchor'
        : 'legacy_approximate';

    var normalized = Object.assign({}, row, {
      type: type,
      kind: type,
      locationType: locationType,
      location_type: locationType,
      privacyConfirmed: row.privacyConfirmed === true,
      parentConfirmed: row.parentConfirmed === true,
      approvalStatus: row.approvalStatus === 'approved' ? 'approved' : 'pending',
    });

    return validCoordinates(finite(normalized.lat), finite(normalized.lng)) &&
      normalized.visibility === 'family_private' &&
      confirmed(normalized)
      ? normalized
      : null;
  }

  function socialRows() {
    var graph = socialGraph();
    var rows = graph ? graph.listPlaces() : [];
    return Array.isArray(rows)
      ? rows.slice(0, MAX_LOCAL_ITEMS).map(normalizeSocialRow).filter(Boolean)
      : [];
  }

  function projectFamilyStats(root) {
    if (!root) return false;

    var rows = socialRows();
    var friendCount = rows.filter(function (row) {
      return row.kind === 'friend';
    }).length;
    var placeCount = rows.filter(function (row) {
      return row.kind === 'mine';
    }).length;
    var stampCount = readLocal(STAMP_KEY).length;

    root.setAttribute('data-kid-family-stats-contract', 'local-private-counts-v1');
    root.setAttribute('data-kid-family-friend-count', String(friendCount));
    root.setAttribute('data-kid-family-place-count', String(placeCount));
    root.setAttribute('data-kid-family-stamp-count', String(stampCount));

    return true;
  }

  function annotationById(id) {
    var graph = socialGraph();
    return graph ? normalizeSocialRow(graph.getPlace(id)) : null;
  }

  function toast(root, message, host) {
    var node = q(root, '[data-kid-toast]');
    if (!node) return false;

    var home = root;
    var target = host && root.contains(host) ? host : home;
    if (node.parentNode !== target) target.appendChild(node);

    node.textContent = message;
    node.hidden = false;
    window.clearTimeout(node.__kidTimer);
    node.__kidTimer = window.setTimeout(function () {
      node.hidden = true;
      if (node.parentNode !== home) home.appendChild(node);
    }, 2800);
    return true;
  }

  function setPressed(node, pressed) {
    if (!node) return;
    node.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    node.classList.toggle('is-active', !!pressed);
  }

  function armDangerButton(button, armedLabel, normalLabel, onConfirm) {
    if (!button || typeof onConfirm !== 'function') return;
    var armed = button.getAttribute('data-kid-danger-armed') === 'true';
    if (armed) {
      window.clearTimeout(button.__kidDangerTimer);
      button.removeAttribute('data-kid-danger-armed');
      button.textContent = normalLabel;
      onConfirm();
      return;
    }
    button.setAttribute('data-kid-danger-armed', 'true');
    button.textContent = armedLabel;
    window.clearTimeout(button.__kidDangerTimer);
    button.__kidDangerTimer = window.setTimeout(function () {
      button.removeAttribute('data-kid-danger-armed');
      button.textContent = normalLabel;
    }, 3000);
  }

  function cleanWorkflowRoute() {
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('intent');
      url.searchParams.delete('step');
      url.searchParams.delete('ref');
      if (url.searchParams.get('source') === 'map') url.searchParams.delete('source');
      window.history.replaceState(
        window.history.state,
        '',
        url.pathname +
          (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') +
          url.hash
      );
    } catch (_) {}
  }

  function setMapView(root, lat, lng, minimumZoom) {
    var renderer = window.LittleGKidMapRenderer;
    if (!renderer || typeof renderer.setView !== 'function') return false;

    var zoom =
      typeof renderer.getZoom === 'function'
        ? Math.max(Number(renderer.getZoom(root)) || 0, Number(minimumZoom) || 0)
        : Number(minimumZoom) || 15;

    return renderer.setView(root, {
      lat: Number(lat),
      lng: Number(lng),
      zoom: zoom,
    });
  }

  function config() {
    return document.querySelector('[data-littleg-kid-map-config]');
  }
  function stationDatasetUrl() {
    var node = config();
    return clean(node && node.getAttribute('data-station-dataset-url'), 500);
  }

  function stationFromFeature(feature) {
    if (
      !feature ||
      feature.type !== 'Feature' ||
      !feature.properties ||
      !feature.geometry ||
      feature.geometry.type !== 'Point'
    )
      return null;
    var coordinates = Array.isArray(feature.geometry.coordinates)
      ? feature.geometry.coordinates
      : [];
    var lng = finite(coordinates[0]);
    var lat = finite(coordinates[1]);
    var properties = feature.properties;
    var stationId = clean(properties.station_id, 120);
    if (!stationId || !validCoordinates(lat, lng)) return null;
    return {
      stationId: stationId,
      nameEn: clean(properties.name_en, 120),
      nameZhHk: clean(properties.name_zh_hk, 120),
      lineIds: Array.isArray(properties.line_ids)
        ? properties.line_ids
            .slice(0, 8)
            .map(function (v) {
              return clean(v, 80);
            })
            .filter(Boolean)
        : [],
      lineColor: clean(properties.line_color, 20),
      mode: clean(properties.mode, 40) || 'mtr',
      lat: lat,
      lng: lng,
    };
  }

  function loadStationDataset() {
    if (stationIndex.length) return Promise.resolve(stationIndex.slice());
    if (stationDatasetPromise) return stationDatasetPromise;
    var url = stationDatasetUrl();
    if (!url || typeof window.fetch !== 'function')
      return Promise.reject(new Error('station_dataset_unavailable'));
    var graph = socialGraph();
    if (!graph || typeof graph.stationIndexReady !== 'function')
      return Promise.reject(new Error('station_index_owner_unavailable'));
    stationDatasetPromise = Promise.all([
      window.fetch(url, { credentials: 'same-origin' }),
      graph.stationIndexReady(),
    ])
      .then(function (values) {
        var response = values[0];
        if (!response.ok) throw new Error('station_dataset_http_' + response.status);
        if (!values[1]) throw new Error('family_station_index_unavailable');
        return response.json();
      })
      .then(function (geojson) {
        if (!geojson || geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features))
          throw new Error('station_dataset_invalid');
        stationIndex = geojson.features.map(stationFromFeature).filter(Boolean);
        return stationIndex.slice();
      })
      .catch(function (error) {
        stationDatasetPromise = null;
        throw error;
      });
    return stationDatasetPromise;
  }

  function stationRank(station, needle) {
    var en = normalizeSearchValue(station.nameEn);
    var zh = normalizeSearchValue(station.nameZhHk);
    var id = normalizeSearchValue(station.stationId.replace(/^mtr-/, ''));
    if (en === needle || zh === needle || id === needle) return 0;
    if (en.indexOf(needle) === 0 || zh.indexOf(needle) === 0 || id.indexOf(needle) === 0) return 1;
    if (en.indexOf(needle) >= 0 || zh.indexOf(needle) >= 0 || id.indexOf(needle) >= 0) return 2;
    return 99;
  }

  function searchStations(query) {
    var needle = normalizeSearchValue(query);
    var rows = stationIndex.slice();
    if (!needle) return rows.slice(0, MAX_STATION_RESULTS);
    return rows
      .map(function (station) {
        return { station: station, rank: stationRank(station, needle) };
      })
      .filter(function (entry) {
        return entry.rank < 99;
      })
      .sort(function (a, b) {
        if (a.rank !== b.rank) return a.rank - b.rank;
        return a.station.nameEn.localeCompare(b.station.nameEn);
      })
      .slice(0, MAX_STATION_RESULTS)
      .map(function (entry) {
        return entry.station;
      });
  }

  function lineLabel(lineId) {
    var labels = {
      'airport-express': 'Airport Express',
      'disneyland-resort-line': 'Disneyland Resort Line',
      'east-rail-line': 'East Rail Line',
      'island-line': 'Island Line',
      'kwun-tong-line': 'Kwun Tong Line',
      'south-island-line': 'South Island Line',
      'tseung-kwan-o-line': 'Tseung Kwan O Line',
      'tsuen-wan-line': 'Tsuen Wan Line',
      'tuen-ma-line': 'Tuen Ma Line',
      'tung-chung-line': 'Tung Chung Line',
    };
    return labels[lineId] || clean(lineId, 80).replace(/-/g, ' ');
  }

  function stationDisplayName(station) {
    if (!station) return '';
    return isChinese() ? station.nameZhHk || station.nameEn : station.nameEn || station.nameZhHk;
  }

  function stationById(stationId) {
    var key = clean(stationId, 120);
    return (
      stationIndex.find(function (station) {
        return station.stationId === key;
      }) || null
    );
  }

  function stationFromRow(row) {
    if (!row || clean(row.locationType || row.location_type, 80) !== 'station_anchor') return null;
    return {
      stationId: clean(row.stationId || row.station_id, 120),
      nameEn: clean(row.stationName || row.station_name, 120),
      nameZhHk: clean(row.stationNameZhHk || row.station_name_zh_hk, 120),
      lineIds: Array.isArray(row.stationLineIds || row.station_line_ids)
        ? (row.stationLineIds || row.station_line_ids).slice(0, 8)
        : [],
      lineColor: '',
      mode: 'mtr',
      lat: finite(row.lat),
      lng: finite(row.lng),
    };
  }

  function setStationModalOpen(root, open, opener) {
    var modal = q(root, '[data-kid-station-modal]');
    if (!modal) return;

    if (open) {
      root.__kidStationModalOpener =
        opener ||
        (document.activeElement && document.activeElement !== document.body
          ? document.activeElement
          : null);

      modal.hidden = false;
      modal.removeAttribute('hidden');
      modal.removeAttribute('aria-hidden');
      modal.classList.add('is-open');

      if (modal.tagName === 'DIALOG' && typeof modal.showModal === 'function') {
        if (!modal.open) modal.showModal();
      } else {
        modal.setAttribute('open', '');
      }

      window.requestAnimationFrame(function () {
        var search = q(modal, '[data-kid-station-search]');
        if (!modal.open || !search) return;

        try {
          search.focus({ preventScroll: true });
        } catch (_) {
          search.focus();
        }
      });

      return;
    }

    if (modal.tagName === 'DIALOG' && typeof modal.close === 'function' && modal.open) {
      modal.close();
    } else {
      modal.removeAttribute('open');
    }

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.hidden = true;
    modal.setAttribute('hidden', '');

    var returnTarget = root.__kidStationModalOpener;
    root.__kidStationModalOpener = null;

    window.setTimeout(function () {
      if (
        !returnTarget ||
        !returnTarget.isConnected ||
        typeof returnTarget.focus !== 'function' ||
        returnTarget.getClientRects().length === 0
      )
        return;

      try {
        returnTarget.focus({ preventScroll: true });
      } catch (_) {
        returnTarget.focus();
      }
    }, 0);
  }

  function appendStationMakiIcon(root, badge) {
    if (!root || !badge) return false;
    var iconTemplate = q(root, '[data-kid-maki-subway-icon]');
    if (!iconTemplate || !iconTemplate.content) return false;
    badge.replaceChildren(iconTemplate.content.cloneNode(true));
    return true;
  }

  function renderStationResults(root, stations) {
    var host = q(root, '[data-kid-station-results]');
    if (!host) return;

    var selectedStationId = clean(
      root && root.__kidSelectedStation && root.__kidSelectedStation.stationId,
      120
    );
    host.replaceChildren();

    stations.forEach(function (station) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'kid-station-result';
      button.setAttribute('role', 'option');
      button.setAttribute('data-kid-station-result', station.stationId);

      var selected = selectedStationId && station.stationId === selectedStationId;
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (selected) button.setAttribute('data-kid-station-selected', 'true');

      button.innerHTML =
        '' +
        '<span class="kid-station-result__badge" aria-hidden="true"></span>' +
        '<span class="kid-station-result__copy"><strong></strong><small></small></span>';

      appendStationMakiIcon(root, button.querySelector('.kid-station-result__badge'));

      var strong = button.querySelector('strong');
      var small = button.querySelector('small');
      if (strong) strong.textContent = stationDisplayName(station);
      if (small) small.textContent = station.lineIds.map(lineLabel).join(' · ');
      host.appendChild(button);
    });

    if (!stations.length) {
      var empty = document.createElement('p');
      empty.className = 'kid-station-modal__empty';
      empty.textContent = isChinese() ? '找不到這個港鐵站。' : 'No station found.';
      host.appendChild(empty);
    }
  }

  function renderStationLines(root) {
    var host = q(root, '[data-kid-station-lines]');
    if (!host || host.childElementCount) return;
    var ids = [];
    stationIndex.forEach(function (station) {
      station.lineIds.forEach(function (id) {
        if (ids.indexOf(id) < 0) ids.push(id);
      });
    });
    ids.forEach(function (id) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'kid-station-line';
      button.setAttribute('data-kid-station-line', id);
      button.textContent = lineLabel(id);
      host.appendChild(button);
    });
  }

  function renderStationPicker(root, query, lineId) {
    var rows = searchStations(query);
    if (lineId)
      rows = rows.filter(function (station) {
        return station.lineIds.indexOf(lineId) >= 0;
      });
    renderStationResults(root, rows);
  }

  function openStationPicker(root, opener) {
    loadStationDataset()
      .then(function () {
        renderStationLines(root);
        var input = q(root, '[data-kid-station-search]');
        if (input) input.value = '';
        root.__kidStationLine = '';
        renderStationPicker(root, '', '');
        setStationModalOpen(root, true, opener);
      })
      .catch(function () {
        toast(
          root,
          isChinese() ? '車站資料暫時未能載入。' : 'Station data is temporarily unavailable.'
        );
      });
  }

  function closeStationPicker(root) {
    setStationModalOpen(root, false);
  }

  function syncAddFormSubmitState(form) {
    if (!form) return;
    var submit = q(form, '[data-kid-add-submit]');
    var label = clean(
      q(form, '[data-kid-annotation-label]') && q(form, '[data-kid-annotation-label]').value,
      80
    );
    var stationId = clean(
      q(form, '[data-kid-station-id]') && q(form, '[data-kid-station-id]').value,
      120
    );
    var locationType = clean(
      q(form, '[data-kid-location-type]') && q(form, '[data-kid-location-type]').value,
      80
    );
    var id = clean(
      q(form, '[data-kid-annotation-id]') && q(form, '[data-kid-annotation-id]').value,
      120
    );
    var existing = id ? annotationById(id) : null;
    var legacyEditable =
      !!existing &&
      existing.locationType === 'legacy_approximate' &&
      locationType === 'legacy_approximate';
    var enabled = !!label && (!!stationId || legacyEditable);
    if (submit) {
      submit.disabled = !enabled;
      submit.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    }
  }

  function selectStation(root, station) {
    if (!root || !station) return false;
    root.__kidSelectedStation = Object.assign({}, station, {
      stationName: station.nameEn,
      stationNameZhHk: station.nameZhHk,
      stationLineIds: station.lineIds.slice(),
      stationLat: station.lat,
      stationLng: station.lng,
      locationType: 'station_anchor',
    });
    var form = q(root, '[data-kid-map-add-form]');
    var stationId = q(form, '[data-kid-station-id]');
    var locationType = q(form, '[data-kid-location-type]');
    var lat = q(form, '[data-kid-annotation-lat]');
    var lng = q(form, '[data-kid-annotation-lng]');
    var precision = q(form, '[data-kid-precision]');
    var pickerLabel = q(form, '[data-kid-station-picker-label]');

    if (stationId) stationId.value = station.stationId;
    if (locationType) locationType.value = 'station_anchor';
    if (lat) lat.value = String(station.lat);
    if (lng) lng.value = String(station.lng);
    if (precision) precision.value = 'station';
    if (pickerLabel) pickerLabel.textContent = stationDisplayName(station);

    syncAddFormSubmitState(form);
    closeStationPicker(root);
    setMapView(root, station.lat, station.lng, 14);

    try {
      document.dispatchEvent(
        new CustomEvent('littleg:kid-map:station-selected', {
          bubbles: true,
          detail: { station: root.__kidSelectedStation },
        })
      );
    } catch (_) {}

    return true;
  }

  function markerDisplayName(row) {
    if (!row) return '';
    if (row.kind === 'mine') return isChinese() ? '我' : 'Me';
    var label = clean(row.label, 80) || (isChinese() ? '朋友' : 'Friend');
    return Array.from(label).slice(0, 8).join('');
  }

  function markerStationSubtitle(row) {
    if (!row || row.locationType !== 'station_anchor') return '';
    var station = clean(
      isChinese() ? row.stationNameZhHk || row.stationName : row.stationName || row.stationNameZhHk,
      30
    );
    return station ? '🚉 ' + station : '';
  }

  function markerAccessibleLabel(row) {
    var name = markerDisplayName(row);
    var subtitle = markerStationSubtitle(row);
    return subtitle ? name + ', ' + subtitle : name;
  }

  function syncSocialMarkerAccessibility(marker, row) {
    if (!marker || typeof marker.getElement !== 'function') return;
    var element = marker.getElement();
    if (!element) return;
    var label = markerAccessibleLabel(row);
    element.setAttribute('aria-label', label);
    element.setAttribute('title', label.replace(', ', ' · '));
  }

  function escapeMarkerHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function friendPinHtml(row) {
    if (!row) return '';

    var isMine = row.kind === 'mine';
    var name = markerDisplayName(row);
    var markerEmoji = clean(row.markerEmoji || row.marker_emoji, 8) || '⭐';
    var avatar = isMine ? '🏠' : markerEmoji;
    var station = markerStationSubtitle(row);

    return (
      '' +
      '<span class="kid-custom-pin kid-custom-pin--' +
      escapeMarkerHtml(row.kind) +
      '" data-location-type="' +
      escapeMarkerHtml(row.locationType) +
      '" aria-hidden="true">' +
      '<span class="kid-custom-pin__avatar">' +
      escapeMarkerHtml(avatar) +
      '</span>' +
      (isMine
        ? '<span class="kid-pin-name">' +
          escapeMarkerHtml(name) +
          '</span>' +
          (station ? '<span class="kid-pin-station">' + escapeMarkerHtml(station) + '</span>' : '')
        : '') +
      '</span>'
    );
  }

  function renderStationAnchoredMarker(row) {
    return friendPinHtml(row);
  }

  function memoryViewOf(root) {
    var value = clean(root && root.getAttribute('data-kid-memory-view'), 24);
    return ['friends', 'alumni', 'class', 'family', 'travel'].indexOf(value) >= 0
      ? value
      : 'friends';
  }

  function memoryViewsOf(row) {
    var values =
      row && Array.isArray(row.memoryViews)
        ? row.memoryViews
        : row && Array.isArray(row.memory_views)
          ? row.memory_views
          : [];
    var allowed = ['friends', 'alumni', 'class', 'family', 'travel'];
    var safe = values
      .map(function (value) {
        return clean(value, 24);
      })
      .filter(function (value, index, list) {
        return allowed.indexOf(value) >= 0 && list.indexOf(value) === index;
      });
    return safe.length ? safe : ['friends'];
  }

  function rowInMemoryView(row, view) {
    var selectedView = view || 'friends';

    if (memoryViewsOf(row).indexOf(selectedView) >= 0) {
      return true;
    }

    // Backward compatibility for classmates saved before Class membership.
    return (
      selectedView === 'class' && !!row && row.kind === 'friend' && row.relationship === 'classmate'
    );
  }

  function visibleSocialRows(root) {
    var filter = root.getAttribute('data-kid-active-filter') || 'all';
    var view = memoryViewOf(root);
    var rows = socialRows().filter(function (row) {
      return rowInMemoryView(row, view);
    });
    if (filter === 'friends')
      return rows.filter(function (row) {
        return row.kind === 'friend';
      });
    if (filter === 'mine')
      return rows.filter(function (row) {
        return row.kind === 'mine';
      });
    return rows;
  }

  function clearSelectedSocialMarker(root) {
    var marker = root && root.__kidSelectedSocialMarker;
    var element = marker && typeof marker.getElement === 'function' ? marker.getElement() : null;

    if (element) {
      var renderer = window.LittleGKidMapRenderer;
      if (renderer && typeof renderer.setMarkerOverlayState === 'function') {
        renderer.setMarkerOverlayState(marker, 'default');
      }
      element.removeAttribute('aria-current');
    }

    if (root) {
      var spotlightRenderer = window.LittleGKidMapRenderer;
      if (spotlightRenderer && typeof spotlightRenderer.clearSelectedAreaSpotlight === 'function') {
        spotlightRenderer.clearSelectedAreaSpotlight(root);
      }
      root.__kidSelectedSocialMarker = null;
      root.removeAttribute('data-kid-selected-social-id');
    }
  }

  function setSelectedSocialMarker(root, marker, row) {
    clearSelectedSocialMarker(root);
    if (!root || !marker || !row) return;

    root.__kidSelectedSocialMarker = marker;
    root.setAttribute('data-kid-selected-social-id', clean(row.id, 120));

    var spotlightRenderer = window.LittleGKidMapRenderer;
    if (spotlightRenderer && typeof spotlightRenderer.setSelectedAreaSpotlight === 'function') {
      spotlightRenderer.setSelectedAreaSpotlight(root, { lat: row.lat, lng: row.lng });
    }

    var element = typeof marker.getElement === 'function' ? marker.getElement() : null;
    if (element) {
      var renderer = window.LittleGKidMapRenderer;
      if (renderer && typeof renderer.setMarkerOverlayState === 'function') {
        renderer.setMarkerOverlayState(marker, 'selected');
      }
      element.setAttribute('aria-current', 'true');
      if (renderer && typeof renderer.ensureMarkerHorizontalSafe === 'function') {
        renderer.ensureMarkerHorizontalSafe(root, marker, { padding: 72, duration: 220 });
      }
    }
  }

  function friendCardRelativeTime(value) {
    var timestamp = Date.parse(value || '');
    if (!Number.isFinite(timestamp)) {
      return isChinese() ? '更新時間未知' : 'Update time unavailable';
    }

    var elapsed = Math.max(0, Date.now() - timestamp);
    var minutes = Math.max(1, Math.floor(elapsed / 60000));
    var hours = Math.floor(elapsed / 3600000);
    var days = Math.floor(elapsed / 86400000);

    var formatter = new Intl.RelativeTimeFormat(document.documentElement.lang || 'en', {
      numeric: 'auto',
      style: 'short',
    });

    if (days > 0) return formatter.format(-days, 'day');
    if (hours > 0) return formatter.format(-hours, 'hour');
    return formatter.format(-minutes, 'minute');
  }

  function syncFriendCardPadding(root) {
    var renderer = window.LittleGKidMapRenderer;
    var map = renderer && typeof renderer.mapFor === 'function' ? renderer.mapFor(root) : null;

    if (!map || typeof map.setPadding !== 'function') return false;

    var card = q(root, '[data-kid-friend-card]');

    if (!card || card.hidden) {
      if (root.__kidFriendPaddingMap === map && root.__kidFriendPaddingBase) {
        map.setPadding(root.__kidFriendPaddingBase);
      }

      root.__kidFriendPaddingMap = null;
      root.__kidFriendPaddingBase = null;
      return true;
    }

    if (root.__kidFriendPaddingMap !== map) {
      root.__kidFriendPaddingMap = map;

      var initial = typeof map.getPadding === 'function' ? map.getPadding() : {};

      root.__kidFriendPaddingBase = {
        top: Number(initial.top) || 0,
        right: Number(initial.right) || 0,
        bottom: Number(initial.bottom) || 0,
        left: Number(initial.left) || 0,
      };
    }

    var baseline = root.__kidFriendPaddingBase;

    var container = map.getContainer && map.getContainer();
    if (!container) return false;

    var mapRect = container.getBoundingClientRect();
    var cardRect = card.getBoundingClientRect();

    var requiredBottom = Math.max(baseline.bottom, Math.ceil(mapRect.bottom - cardRect.top + 16));

    var current = typeof map.getPadding === 'function' ? map.getPadding() : {};

    if (Math.abs((Number(current.bottom) || 0) - requiredBottom) < 1) {
      return true;
    }

    map.setPadding(
      Object.assign({}, baseline, {
        bottom: requiredBottom,
      })
    );

    var row = annotationById(root.__kidOpenSocialId);
    var lat = finite(row && row.lat);
    var lng = finite(row && row.lng);

    if (validCoordinates(lat, lng) && typeof map.easeTo === 'function') {
      var reducedMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      map.easeTo({
        center: [lng, lat],
        duration: reducedMotion ? 0 : 240,
        essential: false,
      });
    }

    return true;
  }

  function watchFriendCardPadding(root, card) {
    if (!root || !card) return;

    if (!root.__kidFriendPaddingResizeHandler) {
      root.__kidFriendPaddingResizeHandler = function () {
        if (root.__kidFriendPaddingFrame) {
          window.cancelAnimationFrame(root.__kidFriendPaddingFrame);
        }

        root.__kidFriendPaddingFrame = window.requestAnimationFrame(function () {
          root.__kidFriendPaddingFrame = 0;
          syncFriendCardPadding(root);
        });
      };

      window.addEventListener('resize', root.__kidFriendPaddingResizeHandler, { passive: true });

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', root.__kidFriendPaddingResizeHandler, {
          passive: true,
        });
      }
    }

    if (!root.__kidFriendCardResizeObserver && window.ResizeObserver) {
      root.__kidFriendCardResizeObserver = new window.ResizeObserver(
        root.__kidFriendPaddingResizeHandler
      );

      root.__kidFriendCardResizeObserver.observe(card);
    }
  }

  function openFriendCard(root, row) {
    var card = q(root, '[data-kid-friend-card]');
    if (!card || !row) return false;

    root.__kidOpenSocialId = row.id;
    card.setAttribute('data-kid-friend-actions-expanded', 'false');

    var titleNode = q(card, '[data-kid-friend-card-title]');
    var timeNode = q(card, '[data-kid-friend-card-time]');
    var stationNode = q(card, '[data-kid-friend-station]');
    var distanceNode = q(card, '[data-kid-friend-distance-text]');
    var placeNode = q(card, '[data-kid-friend-card-place]');
    var moreNode = q(card, '[data-kid-friend-more]');
    var moreToggle = q(card, '[data-kid-friend-actions-toggle]');

    var stationZh = row.locationType === 'station_anchor' ? clean(row.stationNameZhHk, 80) : '';

    var stationEn = row.locationType === 'station_anchor' ? clean(row.stationName, 80) : '';

    var stationName = isChinese() ? stationZh || stationEn : stationEn || stationZh;

    var stationDisplay =
      stationZh && stationEn && stationZh !== stationEn
        ? stationName + ' (' + (isChinese() ? stationEn : stationZh) + ')'
        : stationName;

    if (titleNode) {
      titleNode.textContent = clean(row.label, 80) || (isChinese() ? '朋友' : 'Friend');
    }

    if (timeNode) {
      timeNode.textContent = '· ' + friendCardRelativeTime(row.updatedAt);
      timeNode.title = isChinese()
        ? '地點資料更新時間，並非即時定位'
        : 'Place record update time, not live location';
    }

    if (stationNode) {
      stationNode.textContent =
        stationDisplay ||
        clean(row.placeLabel, 100) ||
        (isChinese() ? '尚未選擇車站' : 'Station not set');
    }

    var mine = socialRows().find(function (candidate) {
      return candidate.kind === 'mine' && candidate.locationType === 'station_anchor';
    });

    var fromLat = finite(mine && mine.lat);
    var fromLng = finite(mine && mine.lng);
    var toLat = finite(row.lat);
    var toLng = finite(row.lng);

    var hasDistance =
      row.kind === 'friend' &&
      row.locationType === 'station_anchor' &&
      validCoordinates(fromLat, fromLng) &&
      validCoordinates(toLat, toLng);

    if (distanceNode) {
      distanceNode.hidden = !hasDistance;

      if (hasDistance) {
        var metres = haversineDistanceMetres(fromLat, fromLng, toLat, toLng);

        distanceNode.textContent = '· ' + (isChinese() ? '約 ' : '≈ ') + formatDistance(metres);

        distanceNode.title = isChinese()
          ? '由已保存的我的地方計算，屬約略直線距離'
          : 'Approximate straight-line distance from your saved place';
      } else {
        distanceNode.textContent = '';
      }
    }

    if (placeNode) {
      var placeDescription = stationName
        ? isChinese()
          ? stationName + ' 港鐵站附近'
          : 'Near ' + stationName + ' MTR Station'
        : isChinese()
          ? '約略位置尚未設定'
          : 'Approximate place not set';

      placeNode.textContent = (isChinese() ? '地點：' : 'Place: ') + placeDescription;
    }

    if (moreNode) moreNode.hidden = true;
    if (moreToggle) moreToggle.setAttribute('aria-expanded', 'false');

    card.setAttribute(
      'data-kid-friend-location-state',
      row.locationType === 'station_anchor' ? 'station-ready' : 'station-missing'
    );

    card.hidden = false;
    root.setAttribute('data-kid-friend-card-open', 'true');

    watchFriendCardPadding(root, card);
    syncFriendCardPadding(root);

    return true;
  }

  function closeFriendCard(root) {
    var card = q(root, '[data-kid-friend-card]');

    if (card) {
      card.hidden = true;
      card.setAttribute('data-kid-friend-actions-expanded', 'false');

      var more = q(card, '[data-kid-friend-more]');
      if (more) more.hidden = true;
    }

    root.removeAttribute('data-kid-friend-card-open');

    syncFriendCardPadding(root);

    root.__kidOpenSocialId = '';
    clearSelectedSocialMarker(root);
    clearRelationshipLine(root);
  }

  function spiderfyOffset(index, total) {
    if (total <= 1) return [0, 0];
    var radius = total <= 4 ? 36 : 44;
    var angle = (Math.PI * 2 * Number(index || 0)) / total - Math.PI / 2;
    return [Math.round(Math.cos(angle) * radius), Math.round(Math.sin(angle) * radius)];
  }

  function addRenderedSocialMarker(root, renderer, row, offset) {
    var lat = finite(row && row.lat);
    var lng = finite(row && row.lng);
    var socialId = clean(row && row.id, 120);

    if (!validCoordinates(lat, lng) || !socialId) return null;

    var markerLabel = markerAccessibleLabel(row);
    var marker = renderer.addFriendMarker(root, {
      id: socialId,
      lat: lat,
      lng: lng,
      offset: Array.isArray(offset) ? offset : [0, 0],
      html: renderStationAnchoredMarker(row),
      ariaLabel: markerLabel.replace(', ', ' · '),
      onClick: function () {
        if (!marker) return;
        setSelectedSocialMarker(root, marker, row);
        openFriendCard(root, row);
      },
    });

    if (marker) {
      syncSocialMarkerAccessibility(marker, row);

      if (
        root.__kidNewSocialId === socialId &&
        typeof renderer.setMarkerOverlayState === 'function'
      ) {
        renderer.setMarkerOverlayState(marker, 'new');
        root.setAttribute('data-kid-new-friend-state', 'active');
        root.__kidNewSocialId = '';

        window.clearTimeout(root.__kidNewFriendTimer);
        root.__kidNewFriendTimer = window.setTimeout(function () {
          renderer.setMarkerOverlayState(marker, 'default');
          root.removeAttribute('data-kid-new-friend-state');
        }, 1400);
      }
    }

    return marker;
  }

  // Cluster only public station anchors by their current screen-space separation.
  // Complete-link grouping prevents a chain of stations becoming one far-away cluster.
  function groupScreenSocialCandidates(map, candidates) {
    var groups = [];
    candidates.forEach(function (candidate) {
      var projected;
      try {
        projected = map.project([candidate.lng, candidate.lat]);
      } catch (_) {
        projected = null;
      }
      var node = {
        candidate: candidate,
        x: projected && Number.isFinite(projected.x) ? projected.x : null,
        y: projected && Number.isFinite(projected.y) ? projected.y : null,
      };
      if (node.x === null || node.y === null) {
        groups.push([node]);
        return;
      }
      var nearest = null,
        nearestDistance = Infinity;
      groups.forEach(function (group) {
        var within = group.every(function (item) {
          if (item.x === null || item.y === null) return false;
          var dx = item.x - node.x,
            dy = item.y - node.y;
          return dx * dx + dy * dy <= 76 * 76;
        });
        if (!within) return;
        var sumX = 0,
          sumY = 0;
        group.forEach(function (item) {
          sumX += item.x;
          sumY += item.y;
        });
        var dx = sumX / group.length - node.x,
          dy = sumY / group.length - node.y;
        var distance = dx * dx + dy * dy;
        if (distance < nearestDistance) {
          nearest = group;
          nearestDistance = distance;
        }
      });
      if (nearest) nearest.push(node);
      else groups.push([node]);
    });
    return groups;
  }
  function bindSocialZoomProjection(root, map) {
    if (!map || typeof map.on !== 'function' || root.__kidSocialZoomMap === map) return;
    if (
      root.__kidSocialZoomMap &&
      root.__kidSocialZoomHandler &&
      typeof root.__kidSocialZoomMap.off === 'function'
    ) {
      root.__kidSocialZoomMap.off('zoomend', root.__kidSocialZoomHandler);
      root.__kidSocialZoomMap.off('resize', root.__kidSocialZoomHandler);
    }
    var refresh = function () {
      root.__kidExpandedDensityIds = null;
      if (root.isConnected) renderSocialPins(root);
    };
    map.on('zoomend', refresh);
    map.on('resize', refresh);
    root.__kidSocialZoomMap = map;
    root.__kidSocialZoomHandler = refresh;
  }

  function renderSocialPins(root) {
    if (!root) return false;

    var renderer = window.LittleGKidMapRenderer;
    if (
      !renderer ||
      typeof renderer.mapFor !== 'function' ||
      typeof renderer.addFriendMarker !== 'function' ||
      typeof renderer.clearFriendMarkers !== 'function' ||
      typeof renderer.addFriendCluster !== 'function' ||
      typeof renderer.clearFriendClusters !== 'function'
    ) {
      return false;
    }

    var map = renderer.mapFor(root);
    if (!map) return false;
    bindSocialZoomProjection(root, map);

    var selectedSocialId = clean(
      root.getAttribute('data-kid-selected-social-id') || root.__kidOpenSocialId,
      120
    );
    // The marker remains 36px; density is determined by pixels at every zoom, not a zoom cutoff.
    var screenMode = typeof map.project === 'function' && typeof map.unproject === 'function';

    clearSelectedSocialMarker(root);
    renderer.clearFriendMarkers(root);
    renderer.clearFriendClusters(root);

    var stationGroups = Object.create(null);
    var singles = [];
    var screenCandidates = [];
    var clusterCount = 0;
    var densityCount = 0;

    visibleSocialRows(root).forEach(function (row) {
      var lat = finite(row && row.lat);
      var lng = finite(row && row.lng);
      var socialId = clean(row && row.id, 120);
      var stationId = clean(row && (row.stationId || row.station_id), 120);
      var stationAnchored =
        clean(row && (row.locationType || row.location_type), 80) === 'station_anchor';
      if (!validCoordinates(lat, lng) || !socialId) return;
      if (row.kind === 'friend' && stationAnchored && stationId) {
        stationGroups[stationId] = stationGroups[stationId] || [];
        stationGroups[stationId].push(row);
        return;
      }
      singles.push(row);
    });

    function renderStationCluster(stationId, group) {
      var anchor = group[0];
      var cluster = renderer.addFriendCluster(root, {
        id: stationId,
        lat: Number(anchor.lat),
        lng: Number(anchor.lng),
        count: group.length,
        ariaLabel: isChinese()
          ? group.length + ' 個朋友在同一港鐵站'
          : group.length + ' friends at one MTR station',
        onClick: function () {
          root.__kidExpandedStationId = stationId;
          renderSocialPins(root);
          try {
            root.dispatchEvent(
              new CustomEvent('littleg:kid-map:station-friends', {
                bubbles: true,
                detail: { stationId: stationId, count: group.length },
              })
            );
          } catch (_) {}
        },
      });
      if (cluster) clusterCount += 1;
    }

    Object.keys(stationGroups).forEach(function (stationId) {
      var group = stationGroups[stationId];
      if (group.length < 2) {
        singles.push(group[0]);
        return;
      }
      var selectedInGroup =
        selectedSocialId &&
        group.some(function (row) {
          return clean(row.id, 120) === selectedSocialId;
        });
      var expanded = root.__kidExpandedStationId === stationId || selectedInGroup;
      if (expanded) {
        group.forEach(function (row, index) {
          addRenderedSocialMarker(root, renderer, row, spiderfyOffset(index, group.length));
        });
        return;
      }
      if (screenMode) {
        screenCandidates.push({
          stationId: stationId,
          rows: group,
          lat: Number(group[0].lat),
          lng: Number(group[0].lng),
        });
      } else {
        renderStationCluster(stationId, group);
      }
    });

    singles.forEach(function (row) {
      var selected = selectedSocialId && clean(row.id, 120) === selectedSocialId;
      var anchored = clean(row.locationType || row.location_type, 80) === 'station_anchor';
      if (screenMode && row.kind === 'friend' && anchored && !selected) {
        screenCandidates.push({
          stationId: '',
          rows: [row],
          lat: Number(row.lat),
          lng: Number(row.lng),
        });
      } else {
        addRenderedSocialMarker(root, renderer, row, [0, 0]);
      }
    });

    if (screenMode) {
      groupScreenSocialCandidates(map, screenCandidates).forEach(function (nodes, index) {
        if (nodes.length === 1) {
          var only = nodes[0].candidate;
          if (only.rows.length > 1) renderStationCluster(only.stationId, only.rows);
          else addRenderedSocialMarker(root, renderer, only.rows[0], [0, 0]);
          return;
        }
        var pointX = 0,
          pointY = 0,
          members = [];
        nodes.forEach(function (node) {
          pointX += node.x;
          pointY += node.y;
          members = members.concat(node.candidate.rows);
        });
        var memberIds = members
          .map(function (row) {
            return clean(row.id, 120);
          })
          .filter(Boolean)
          .sort();
        var expandedIds = root.__kidExpandedDensityIds;
        var expanded =
          Array.isArray(expandedIds) &&
          expandedIds.length === memberIds.length &&
          expandedIds.every(function (id, itemIndex) {
            return id === memberIds[itemIndex];
          });
        if (expanded) {
          members.forEach(function (row, itemIndex) {
            addRenderedSocialMarker(root, renderer, row, spiderfyOffset(itemIndex, members.length));
          });
          return;
        }
        var center = map.unproject([pointX / nodes.length, pointY / nodes.length]);
        var maxZoom =
          typeof map.getMaxZoom === 'function' && Number.isFinite(Number(map.getMaxZoom()))
            ? Math.min(20, Number(map.getMaxZoom()))
            : 20;
        var zoom = typeof map.getZoom === 'function' ? Number(map.getZoom()) || 0 : 0;
        var count = members.length;
        var marker = renderer.addFriendCluster(root, {
          id: 'density-' + index,
          lat: center.lat,
          lng: center.lng,
          count: count,
          ariaLabel:
            zoom >= maxZoom - 0.05
              ? isChinese()
                ? '展開附近 ' + count + ' 個朋友'
                : 'Expand ' + count + ' nearby friends'
              : isChinese()
                ? count + ' 個朋友在附近，按一下放大地圖'
                : count + ' friends nearby, activate to zoom in',
          onClick: function () {
            if (zoom >= maxZoom - 0.05 || typeof renderer.setView !== 'function') {
              root.__kidExpandedDensityIds = memberIds;
              renderSocialPins(root);
              return;
            }
            renderer.setView(root, {
              lat: center.lat,
              lng: center.lng,
              zoom: Math.min(maxZoom, Math.max(14.25, zoom + 2)),
            });
          },
        });
        if (marker) {
          clusterCount += 1;
          densityCount += 1;
        }
      });
    }

    if (selectedSocialId) {
      var selectedMarker =
        root.__kidSocialMarkersById && root.__kidSocialMarkersById[selectedSocialId];
      var selectedRow = annotationById(selectedSocialId);
      if (selectedMarker && selectedRow) setSelectedSocialMarker(root, selectedMarker, selectedRow);
      else clearSelectedSocialMarker(root);
    } else {
      root.__kidSelectedSocialMarker = null;
    }

    root.setAttribute('data-kid-social-count', String(visibleSocialRows(root).length));
    root.setAttribute('data-kid-memory-view-count', String(visibleSocialRows(root).length));
    root.setAttribute('data-kid-social-cluster-count', String(clusterCount));
    root.setAttribute('data-kid-density-cluster-count', String(densityCount));
    projectFamilyStats(root);
    return true;
  }
  function waitForSocialRenderer(root, attempt) {
    if (renderSocialPins(root)) return;
    if ((attempt || 0) >= 120) return;
    window.setTimeout(function () {
      waitForSocialRenderer(root, (attempt || 0) + 1);
    }, 100);
  }

  function bindMemoryViewProjection(root) {
    if (!root || root.__kidMemoryViewProjectionBound) return;
    root.__kidMemoryViewProjectionBound = true;

    root.addEventListener('littleg:kid-map:memory-view-change', function () {
      clearPublicExplore(root);

      var nearby = q(root, '[data-kid-nearby-details]');
      if (nearby && nearby.open) nearby.open = false;

      closeFriendCard(root);
      root.__kidExpandedStationId = '';
      renderSocialPins(root);
    });
  }

  /* Focus Trap & Restoration Engine */
  function setModalOpen(modal, open, opener) {
    if (!modal) return;
    var root = modal.closest && modal.closest('[data-littleg-kid-map]');

    if (open) {
      lastActiveFocusElement =
        opener ||
        (document.activeElement && document.activeElement !== document.body
          ? document.activeElement
          : null);
      if (typeof modal.showModal === 'function') {
        if (!modal.open) modal.showModal();
      } else {
        modal.setAttribute('open', '');
      }
      if (root) root.setAttribute('data-kid-add-sheet-open', 'true');

      var heading = modal.querySelector('h2, h3');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        window.requestAnimationFrame(function () {
          heading.focus({ preventScroll: true });
        });
      }
      return;
    }

    if (typeof modal.close === 'function') {
      if (modal.open) modal.close();
    } else {
      modal.removeAttribute('open');
    }
    if (root) root.removeAttribute('data-kid-add-sheet-open');

    var targetToFocus = opener || lastActiveFocusElement;
    if (targetToFocus && typeof targetToFocus.focus === 'function') {
      targetToFocus.focus();
      lastActiveFocusElement = null;
    }
  }

  function resetStationForm(root, form) {
    root.__kidSelectedStation = null;
    var stationId = q(form, '[data-kid-station-id]');
    var locationType = q(form, '[data-kid-location-type]');
    var lat = q(form, '[data-kid-annotation-lat]');
    var lng = q(form, '[data-kid-annotation-lng]');
    var precision = q(form, '[data-kid-precision]');
    var pickerLabel = q(form, '[data-kid-station-picker-label]');

    if (stationId) stationId.value = '';
    if (locationType) locationType.value = 'station_anchor';
    if (lat) lat.value = '';
    if (lng) lng.value = '';
    if (precision) precision.value = 'station';
    if (pickerLabel) pickerLabel.textContent = isChinese() ? '選擇港鐵站' : 'Choose a station';
  }

  function openAnnotationModal(root, data, opener) {
    var modal = q(root, '[data-kid-map-add-modal]');
    var form = q(root, '[data-kid-map-add-form]');
    if (!modal || !form || !data) return;

    var kind = data.kind === 'mine' || data.type === 'mine' ? 'mine' : 'friend';
    var existing = data.id ? annotationById(data.id) : null;
    var titleNode = q(modal, '[data-kid-add-modal-title]');
    var labelTitle = q(modal, '[data-kid-annotation-label-title]');
    var labelInput = q(modal, '[data-kid-annotation-label]');
    var markerEmojiField = q(modal, '[data-kid-marker-emoji-field]');
    var markerEmojiInput = q(modal, '[data-kid-marker-emoji]');
    var relationshipField = q(modal, '[data-kid-relationship-field]');
    var relationshipInput = q(modal, '[data-kid-relationship-type]');
    var alumniField = q(modal, '[data-kid-memory-view-alumni-field]');
    var alumniInput = q(modal, '[data-kid-memory-view-alumni]');
    var familyInput = q(modal, '[data-kid-memory-view-family]');
    var kindInput = q(modal, '[data-kid-annotation-kind]');
    var idInput = q(modal, '[data-kid-annotation-id]');
    var locationType = q(modal, '[data-kid-location-type]');
    var stationId = q(modal, '[data-kid-station-id]');
    var latInput = q(modal, '[data-kid-annotation-lat]');
    var lngInput = q(modal, '[data-kid-annotation-lng]');
    var precisionInput = q(modal, '[data-kid-precision]');
    var pickerLabel = q(modal, '[data-kid-station-picker-label]');

    if (!kindInput || !idInput || !latInput || !lngInput) return;

    kindInput.value = kind;
    idInput.value = data.id || '';
    if (titleNode) {
      titleNode.textContent = existing
        ? kind === 'mine'
          ? isChinese()
            ? '編輯地點'
            : 'Edit place'
          : isChinese()
            ? '編輯朋友'
            : 'Edit friend'
        : kind === 'mine'
          ? isChinese()
            ? '新增地點'
            : 'Add a place'
          : isChinese()
            ? '新增朋友'
            : 'Add a friend';
    }
    if (labelTitle)
      labelTitle.textContent =
        kind === 'mine' ? (isChinese() ? '地點名稱' : 'Place name') : isChinese() ? '名字' : 'Name';
    if (labelInput) {
      labelInput.value = data.label || '';
      labelInput.placeholder = kind === 'mine' ? (isChinese() ? '我的地方' : 'My place') : 'Emma';
    }
    if (markerEmojiField) markerEmojiField.hidden = kind !== 'friend';
    if (markerEmojiInput)
      markerEmojiInput.value =
        kind === 'friend' ? data.markerEmoji || data.marker_emoji || '⭐' : '⭐';
    if (relationshipField) relationshipField.hidden = kind !== 'friend';
    if (relationshipInput) {
      relationshipInput.value =
        data.relationship ||
        (!existing && kind === 'friend' && memoryViewOf(root) === 'family'
          ? 'family'
          : !existing && kind === 'friend' && memoryViewOf(root) === 'class'
            ? 'classmate'
            : 'friend');
    }
    var existingViews = memoryViewsOf(existing || data);
    if (alumniField) alumniField.hidden = kind !== 'friend';
    if (alumniInput) {
      alumniInput.checked =
        kind === 'friend' &&
        (existingViews.indexOf('alumni') >= 0 || (!existing && memoryViewOf(root) === 'alumni'));
    }
    if (familyInput) {
      familyInput.checked =
        existingViews.indexOf('family') >= 0 ||
        (!existing && memoryViewOf(root) === 'family') ||
        (!existing &&
          kind === 'friend' &&
          relationshipInput &&
          relationshipInput.value === 'family');
    }
    if (relationshipInput && !relationshipInput.__kidFamilySyncBound) {
      relationshipInput.__kidFamilySyncBound = true;
      relationshipInput.addEventListener('change', function () {
        var family = q(modal, '[data-kid-memory-view-family]');
        if (family && relationshipInput.value === 'family') family.checked = true;
      });
    }

    resetStationForm(root, form);
    if (existing && existing.locationType === 'station_anchor') {
      var station = stationFromRow(existing);
      root.__kidSelectedStation = station;
      if (stationId) stationId.value = station.stationId;
      if (locationType) locationType.value = 'station_anchor';
      latInput.value = String(existing.lat);
      lngInput.value = String(existing.lng);
      if (precisionInput) precisionInput.value = 'station';
      if (pickerLabel)
        pickerLabel.textContent = isChinese()
          ? existing.stationNameZhHk || existing.stationName
          : existing.stationName || existing.stationNameZhHk;
    } else if (existing && existing.locationType === 'legacy_approximate') {
      if (locationType) locationType.value = 'legacy_approximate';
      latInput.value = String(existing.lat);
      lngInput.value = String(existing.lng);
      if (precisionInput) precisionInput.value = 'approximate';
      if (pickerLabel)
        pickerLabel.textContent = isChinese()
          ? '選新車站以更新定位'
          : 'Choose a station to update location';
    }

    syncAddFormSubmitState(form);
    setModalOpen(modal, true, opener);

    window.setTimeout(function () {
      var stationPicker = q(modal, '[data-kid-station-picker]');
      if (stationPicker) stationPicker.focus();
    }, 0);
  }

  function openSelectedAs(root, kind, opener) {
    openAnnotationModal(root, { kind: kind, type: kind }, opener);
  }

  function saveFriendWithStationAnchor(root, form) {
    var graph = socialGraph();
    var privacyRuntime = window.LittleGKidLocationPrivacy;
    if (
      !graph ||
      !privacyRuntime ||
      privacyRuntime.defaultStrategy !== 'station_anchor' ||
      privacyRuntime.rawHomeCoordinatePersistence !== false
    ) {
      toast(
        root,
        isChinese()
          ? '私人位置保護尚未準備好，暫時不能儲存。'
          : 'Private location protection is not ready.'
      );
      return false;
    }

    var formData = new FormData(form);
    var kind = formData.get('annotation_kind') === 'mine' ? 'mine' : 'friend';
    var id = clean(formData.get('annotation_id'), 120);
    var existing = id ? graph.getPlace(id) : null;
    var label = clean(formData.get('annotation_label'), 80);
    var relationship = clean(formData.get('relationship_type'), 30) || 'friend';
    var markerEmoji = clean(formData.get('marker_emoji'), 8) || '⭐';
    var alumniChecked = kind === 'friend' && formData.get('memory_view_alumni') === 'alumni';
    var familyChecked = formData.get('memory_view_family') === 'family';
    var priorMemoryViews = memoryViewsOf(existing || {});
    var previousRelationship = clean(existing && existing.relationship, 30);

    var removeDerivedClass = previousRelationship === 'classmate' && relationship !== 'classmate';

    var memoryViews =
      kind === 'friend'
        ? priorMemoryViews.filter(function (view) {
            return (
              view !== 'alumni' &&
              view !== 'family' &&
              view !== 'friends' &&
              !(view === 'class' && removeDerivedClass)
            );
          })
        : priorMemoryViews.filter(function (view) {
            return view !== 'family' && view !== 'alumni' && view !== 'class';
          });

    if (kind === 'friend') {
      memoryViews.unshift('friends');

      if (alumniChecked) {
        memoryViews.push('alumni');
      }

      if (relationship === 'classmate' && memoryViews.indexOf('class') < 0) {
        memoryViews.push('class');
      }
    }
    if (memoryViews.indexOf('friends') < 0) memoryViews.unshift('friends');
    if (familyChecked && memoryViews.indexOf('family') < 0) memoryViews.push('family');
    var stationId = clean(formData.get('station_id'), 120);
    var selectedStation =
      root.__kidSelectedStation && root.__kidSelectedStation.stationId === stationId
        ? root.__kidSelectedStation
        : stationById(stationId);
    var now = new Date().toISOString();

    if (!label) {
      toast(root, isChinese() ? '請輸入名稱。' : 'Enter a name.');
      return false;
    }
    if (!selectedStation) {
      if (
        existing &&
        clean(existing.locationType || existing.location_type, 80) === 'legacy_approximate'
      ) {
        var legacySaved = graph.upsertPlace(
          Object.assign({}, existing, {
            id: id,
            type: kind,
            kind: kind,
            label: label,
            relationship: kind === 'friend' ? relationship : '',
            markerEmoji: kind === 'friend' ? markerEmoji : '',
            marker_emoji: kind === 'friend' ? markerEmoji : '',
            memoryViews: memoryViews,
            memory_views: memoryViews.slice(),
            visibility: 'family_private',
            parentConfirmed: true,
            privacyConfirmed: true,
            approvalStatus: 'approved',
            updatedAt: now,
          })
        );
        if (!legacySaved) return false;
        renderSocialPins(root);
        setModalOpen(q(root, '[data-kid-map-add-modal]'), false);
        closeFriendCard(root);
        cleanWorkflowRoute();
        toast(root, isChinese() ? '🔒 已保存舊版約略地點。' : '🔒 Legacy approximate place saved.');
        return true;
      }
      toast(root, isChinese() ? '請先選擇港鐵站。' : 'Choose an MTR station first.');
      return false;
    }

    var row = {
      id: id || uid(kind === 'friend' ? 'fr' : 'pl'),
      type: kind,
      kind: kind,
      label: label,
      relationship: kind === 'friend' ? relationship : '',
      markerEmoji: kind === 'friend' ? markerEmoji : '',
      marker_emoji: kind === 'friend' ? markerEmoji : '',
      memoryViews: memoryViews,
      memory_views: memoryViews.slice(),
      locationType: 'station_anchor',
      stationId: selectedStation.stationId,
      stationName: selectedStation.nameEn || selectedStation.stationName || '',
      stationNameZhHk: selectedStation.nameZhHk || selectedStation.stationNameZhHk || '',
      stationLineIds: (selectedStation.lineIds || selectedStation.stationLineIds || []).slice(0, 8),
      lat: Number(selectedStation.lat != null ? selectedStation.lat : selectedStation.stationLat),
      lng: Number(selectedStation.lng != null ? selectedStation.lng : selectedStation.stationLng),
      precision: 'station',
      visibility: 'family_private',
      privacyConfirmed: true,
      // Legacy Customer Memory schema: owner-saved private record, not a separate parent review.
      parentConfirmed: true,
      approvalStatus: 'approved',
      publicSearchable: false,
      childPublicProfile: false,
      realtimeChild: false,
      createdAt: existing && existing.createdAt ? existing.createdAt : now,
      updatedAt: now,
    };

    var saved = graph.upsertPlace(row);
    if (!saved) {
      toast(
        root,
        isChinese() ? '瀏覽器沒有允許本機儲存。' : 'Local browser storage is unavailable.'
      );
      return false;
    }

    if (!existing && kind === 'friend') root.__kidNewSocialId = row.id;

    var savedFilter = kind === 'mine' ? 'mine' : 'friends';
    root.setAttribute('data-kid-active-filter', savedFilter);
    qa(root, '[data-kid-filter]').forEach(function (item) {
      setPressed(item, item.getAttribute('data-kid-filter') === savedFilter);
    });

    renderSocialPins(root);
    setModalOpen(q(root, '[data-kid-map-add-modal]'), false);
    closeStationPicker(root);
    closeFriendCard(root);
    cleanWorkflowRoute();
    toast(root, isChinese() ? '🔒 已用港鐵站保存。' : '🔒 Saved with station anchor.');
    return true;
  }

  function saveAnnotation(root, form) {
    return saveFriendWithStationAnchor(root, form);
  }

  function deleteAnnotation(root, id) {
    var graph = socialGraph();
    if (!graph || !graph.getPlace(id) || !graph.removePlace(id)) return false;
    closeFriendCard(root);
    renderSocialPins(root);
    toast(root, isChinese() ? '已移除。' : 'Removed.');
    return true;
  }

  function formatDistance(metres) {
    var value = Number(metres || 0);
    if (value < 1000) return Math.max(1, Math.round(value)) + ' m';
    return (value / 1000).toFixed(value >= 10000 ? 0 : 1) + ' km';
  }

  function haversineDistanceMetres(lat1, lng1, lat2, lng2) {
    var rad = Math.PI / 180;
    var earth = 6371008.8;
    var dLat = (lat2 - lat1) * rad;
    var dLng = (lng2 - lng1) * rad;
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return earth * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(Math.max(0, 1 - a)));
  }

  function clearRelationshipLine(root) {
    var renderer = window.LittleGKidMapRenderer;
    if (!renderer || typeof renderer.clearRelationshipLine !== 'function') return false;
    return renderer.clearRelationshipLine(root);
  }

  function drawRelationshipLine(root, row) {
    var renderer = window.LittleGKidMapRenderer;
    if (!renderer || typeof renderer.setRelationshipLine !== 'function' || !row) return false;

    var mine = socialRows().find(function (candidate) {
      return candidate.kind === 'mine';
    });
    if (!mine) {
      toast(root, isChinese() ? '先加入一個「我的地方」。' : 'Add one of My places first.');
      return false;
    }

    var fromLat = finite(mine.lat);
    var fromLng = finite(mine.lng);
    var toLat = finite(row.lat);
    var toLng = finite(row.lng);

    if (!validCoordinates(fromLat, fromLng) || !validCoordinates(toLat, toLng)) return false;

    var distance = haversineDistanceMetres(fromLat, fromLng, toLat, toLng);
    return renderer.setRelationshipLine(root, {
      from: { lat: fromLat, lng: fromLng },
      to: { lat: toLat, lng: toLng },
      label: (isChinese() ? '距離 ' : 'Distance ') + formatDistance(distance),
    });
  }

  function openSocialPlace(root, id, options) {
    var row = annotationById(id);
    if (!row) return false;

    var filter = row.kind === 'mine' ? 'mine' : 'friends';
    root.setAttribute('data-kid-active-filter', filter);

    qa(root, '[data-kid-filter]').forEach(function (item) {
      setPressed(item, item.getAttribute('data-kid-filter') === filter);
    });

    root.__kidOpenSocialId = clean(row.id, 120);
    renderSocialPins(root);

    var socialId = clean(row.id, 120);
    var marker = socialId && root.__kidSocialMarkersById && root.__kidSocialMarkersById[socialId];

    if (marker) {
      setSelectedSocialMarker(root, marker, row);
    } else {
      clearSelectedSocialMarker(root);
    }

    setMapView(root, row.lat, row.lng, 15);
    openFriendCard(root, row);

    if (options && options.relationship) {
      drawRelationshipLine(root, row);
    }

    return true;
  }

  function bindStationPicker(root) {
    var picker = q(root, '[data-kid-station-picker]');
    var close = q(root, '[data-kid-station-close]');
    var search = q(root, '[data-kid-station-search]');
    var results = q(root, '[data-kid-station-results]');
    var lines = q(root, '[data-kid-station-lines]');

    if (picker)
      picker.addEventListener('click', function () {
        openStationPicker(root, picker);
      });
    if (close)
      close.addEventListener('click', function () {
        closeStationPicker(root);
      });

    var stationModal = q(root, '[data-kid-station-modal]');
    if (stationModal && !stationModal.__kidCancelBound) {
      stationModal.__kidCancelBound = true;
      stationModal.addEventListener('cancel', function (event) {
        event.preventDefault();
        closeStationPicker(root);
      });
    }

    if (search)
      search.addEventListener('input', function () {
        renderStationPicker(root, search.value, root.__kidStationLine || '');
      });
    if (results)
      results.addEventListener('click', function (event) {
        var button = event.target.closest('[data-kid-station-result]');
        if (!button) return;
        var station = stationById(button.getAttribute('data-kid-station-result'));
        if (station) selectStation(root, station);
      });
    if (lines)
      lines.addEventListener('click', function (event) {
        var button = event.target.closest('[data-kid-station-line]');
        if (!button) return;
        var next = button.getAttribute('data-kid-station-line');
        root.__kidStationLine = root.__kidStationLine === next ? '' : next;
        qa(lines, '[data-kid-station-line]').forEach(function (item) {
          setPressed(item, item.getAttribute('data-kid-station-line') === root.__kidStationLine);
        });
        renderStationPicker(root, search ? search.value : '', root.__kidStationLine);
      });
  }

  function bindSocialActions(root) {
    qa(root, '[data-kid-add-kind]').forEach(function (button) {
      button.addEventListener('click', function () {
        openAnnotationModal(
          root,
          { kind: button.getAttribute('data-kid-add-kind') === 'mine' ? 'mine' : 'friend' },
          button
        );
      });
    });

    qa(root, '[data-kid-mark-selected]').forEach(function (button) {
      button.addEventListener('click', function () {
        openSelectedAs(root, button.getAttribute('data-kid-mark-selected'), button);
      });
    });

    var modal = q(root, '[data-kid-map-add-modal]');
    var form = q(root, '[data-kid-map-add-form]');
    var modalClose = q(root, '[data-kid-add-modal-close]');
    var modalCancel = q(root, '[data-kid-add-modal-cancel]');

    function closeModal() {
      setModalOpen(modal, false);
      closeStationPicker(root);
      cleanWorkflowRoute();
    }
    if (modalClose) modalClose.addEventListener('click', closeModal);
    if (modalCancel) modalCancel.addEventListener('click', closeModal);

    if (form) {
      var labelInput = q(form, '[data-kid-annotation-label]');
      if (labelInput)
        labelInput.addEventListener('input', function () {
          syncAddFormSubmitState(form);
        });
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;
        saveAnnotation(root, form);
      });
    }

    var cardClose = q(root, '[data-kid-friend-card-close]');
    if (cardClose)
      cardClose.addEventListener('click', function () {
        closeFriendCard(root);
      });

    var edit = q(root, '[data-kid-friend-edit]');
    if (edit)
      edit.addEventListener('click', function () {
        var row = annotationById(root.__kidOpenSocialId);
        if (!row) return;
        closeFriendCard(root);
        window.setTimeout(function () {
          openAnnotationModal(root, row, edit);
        }, 0);
      });

    var remove = q(root, '[data-kid-friend-delete]');
    if (remove)
      remove.addEventListener('click', function () {
        var row = annotationById(root.__kidOpenSocialId);
        if (!row) return;
        armDangerButton(
          remove,
          isChinese() ? '再按一次刪除' : 'Tap again to delete',
          isChinese() ? '刪除' : 'Delete',
          function () {
            deleteAnnotation(root, row.id);
          }
        );
      });

    var distance = q(root, '[data-kid-friend-distance]');
    if (distance)
      distance.addEventListener('click', function () {
        var row = annotationById(root.__kidOpenSocialId);
        if (row) drawRelationshipLine(root, row);
      });
  }

  /* K04/K06 — Official public Explore.
   * Core API is the only frontend transport.
   * Play and Learn share one ephemeral result/card/pin owner.
   * Public state never enters FamilyData.
   */

  var PUBLIC_EXPLORE_TYPE = 'littleg-public-explore';

  var PUBLIC_SOURCE_LABELS = Object.freeze({
    'lcsd-child-play-rooms': ['康文署兒童遊戲室', 'LCSD Children’s Play Rooms'],
    'lcsd-inclusive-play': ['康文署共融遊樂設施', 'LCSD Inclusive Play'],
    'lcsd-model-car-play-area': ['康文署模型車遊樂場', 'LCSD Model Car Play Area'],
    'edb-schools': ['教育局學校資料', 'Education Bureau schools'],
    'lcsd-libraries': ['香港公共圖書館', 'Hong Kong Public Libraries'],
  });

  var PUBLIC_GROUP_SOURCES = Object.freeze({
    play: Object.freeze([
      'lcsd-child-play-rooms',
      'lcsd-inclusive-play',
      'lcsd-model-car-play-area',
    ]),
    learn: Object.freeze(['edb-schools', 'lcsd-libraries']),
  });

  function publicWords(zhHk, zhCn, en) {
    var lang = document.documentElement.lang || '';

    if (/^zh-(cn|hans)\b/i.test(lang)) return zhCn || zhHk;
    return /^zh\b/i.test(lang) ? zhHk : en;
  }

  function publicLocalizedName(value) {
    if (!value || typeof value !== 'object') return '';

    return clean(isChinese() ? value.zh_hk || value.en : value.en || value.zh_hk, 180);
  }

  function publicSourceName(id) {
    var labels = PUBLIC_SOURCE_LABELS[id];

    return labels ? labels[isChinese() ? 0 : 1] : clean(id, 120);
  }

  function publicPlaceName(row) {
    return publicLocalizedName(row.name) || clean(row.display_name, 180);
  }

  function publicPlaceType(row) {
    var sourceId = row.source && row.source.id;

    if (sourceId === 'lcsd-child-play-rooms') {
      return publicWords('室內遊戲室', '室内游戏室', 'Playroom');
    }

    if (sourceId === 'lcsd-inclusive-play') {
      return publicWords('共融遊樂設施', '共融游乐设施', 'Inclusive play');
    }

    if (sourceId === 'lcsd-model-car-play-area') {
      return publicWords('模型車遊樂場', '模型车游乐场', 'Model car play area');
    }

    if (sourceId === 'edb-schools') {
      return publicWords('學校', '学校', 'School');
    }

    if (sourceId === 'lcsd-libraries') {
      return publicWords('公共圖書館', '公共图书馆', 'Public library');
    }

    return '';
  }

  function publicPlaceMeta(row) {
    return [publicPlaceType(row), publicLocalizedName(row.district)].filter(Boolean).join(' · ');
  }

  function publicOfficialUrl(value) {
    try {
      var url = new URL(String(value || ''));

      if (url.protocol !== 'https:') return '';
      if (url.username || url.password) return '';

      var host = url.hostname.toLowerCase();

      if (host !== 'gov.hk' && !host.endsWith('.gov.hk')) {
        return '';
      }

      return url.toString();
    } catch (_) {
      return '';
    }
  }

  function publicCoreUrl(detail) {
    var node = config();
    if (!node) return null;

    var base = clean(node.getAttribute('data-gproperty-api-base'), 300).replace(/\/+$/, '');

    if (base !== 'https://api.glhksolution.com') return null;

    var path = detail
      ? node.getAttribute('data-kid-public-explore-detail-path')
      : node.getAttribute('data-kid-public-explore-path');

    var expected = detail ? '/api/explore/place' : '/api/explore';

    if (path !== expected) return null;

    return new URL(expected, base);
  }

  function normalizePublicPlace(item, sourceGroup) {
    if (!item || typeof item !== 'object') return null;

    var id = clean(item.id, 160);
    var sourceId = clean(item.source && item.source.id, 120);
    var latitude = finite(item.location && item.location.latitude);
    var longitude = finite(item.location && item.location.longitude);
    var allowedSources = PUBLIC_GROUP_SOURCES[sourceGroup];

    if (!id || !publicPlaceName(item) || !Array.isArray(allowedSources)) {
      return null;
    }

    if (allowedSources.indexOf(sourceId) < 0) {
      return null;
    }

    if (
      latitude === null ||
      longitude === null ||
      latitude < 20 ||
      latitude > 24 ||
      longitude < 112 ||
      longitude > 116
    ) {
      return null;
    }

    return item;
  }

  function setPublicPlacesState(root, state, message, sourceGroup) {
    var panel = q(root, '[data-kid-public-places]');
    var status = q(root, '[data-kid-public-places-status]');
    var results = q(root, '[data-kid-public-places-results]');
    var trigger = q(root, '[data-kid-public-source-group="' + sourceGroup + '"]');

    if (!panel || !status || !results) return;

    root.__kidPublicSourceGroup = sourceGroup;
    panel.hidden = false;
    panel.setAttribute('data-kid-public-places-state', state);
    panel.setAttribute('data-kid-public-places-view', sourceGroup);

    status.textContent = message;

    results.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');

    qa(root, '[data-kid-public-source-group]').forEach(function (button) {
      button.setAttribute(
        'aria-busy',
        button === trigger && state === 'loading' ? 'true' : 'false'
      );
    });
  }

  function clearPublicSelection(root, restoreFocus) {
    var previousId = root.__kidPublicSelectedId || '';

    root.__kidPublicDetailToken = (root.__kidPublicDetailToken || 0) + 1;

    if (root.__kidPublicDetailAbort) {
      root.__kidPublicDetailAbort.abort();
      root.__kidPublicDetailAbort = null;
    }

    root.__kidPublicSelectedId = '';

    var selection = q(root, '[data-kid-selected-public-place]');
    var link = q(root, '[data-kid-selected-public-place-link]');

    if (selection) selection.hidden = true;
    if (link) {
      link.hidden = true;
      link.removeAttribute('href');
    }

    qa(root, '[data-kid-public-card-id]').forEach(function (button) {
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-expanded', 'false');
    });

    var renderer = window.LittleGKidMapRenderer;

    if (renderer && typeof renderer.setPublicExploreSelection === 'function') {
      renderer.setPublicExploreSelection(root, '');
    }

    if (restoreFocus && previousId) {
      var previousCard = qa(root, '[data-kid-public-card-id]').find(function (button) {
        return button.getAttribute('data-kid-public-card-id') === previousId;
      });

      if (previousCard) previousCard.focus({ preventScroll: true });
    }
  }

  function clearPublicExplore(root, keepPanel) {
    if (!(root instanceof HTMLElement)) return;

    root.__kidPublicSequence = (root.__kidPublicSequence || 0) + 1;

    window.clearTimeout(root.__kidPublicPinsTimer);
    root.__kidPublicPinsTimer = 0;

    if (root.__kidPublicListAbort) {
      root.__kidPublicListAbort.abort();
      root.__kidPublicListAbort = null;
    }

    clearPublicSelection(root, false);

    var renderer = window.LittleGKidMapRenderer;

    if (renderer && typeof renderer.clearNearbyPoints === 'function') {
      renderer.clearNearbyPoints(root, PUBLIC_EXPLORE_TYPE);
    }

    root.__kidPublicPlaces = Object.create(null);
    root.__kidPublicRows = [];

    var panel = q(root, '[data-kid-public-places]');
    var results = q(root, '[data-kid-public-places-results]');
    var count = q(root, '[data-kid-public-places-count]');
    var sources = q(root, '[data-kid-public-sources]');
    var empty = q(root, '[data-kid-public-places-empty]');
    var status = q(root, '[data-kid-public-places-status]');
    var sourceGroup = clean(root.__kidPublicSourceGroup, 20);

    if (results) {
      results.replaceChildren();
      results.setAttribute('aria-busy', 'false');
    }

    if (sources) {
      sources.replaceChildren();
      sources.hidden = true;
    }

    if (count) {
      count.textContent = '0';
      count.hidden = true;
    }

    if (empty) empty.hidden = true;
    if (status) status.textContent = '';

    qa(root, '[data-kid-public-source-group]').forEach(function (button) {
      button.setAttribute('aria-busy', 'false');
    });

    if (panel) {
      panel.hidden = keepPanel !== true;
      panel.setAttribute('data-kid-public-places-state', 'idle');
      panel.setAttribute('data-kid-public-places-view', keepPanel ? sourceGroup : '');
    }

    if (!keepPanel) root.__kidPublicSourceGroup = '';
  }

  function renderPublicSources(root, sources) {
    var host = q(root, '[data-kid-public-sources]');
    if (!host) return;

    host.replaceChildren();

    sources.forEach(function (source) {
      if (!source || !PUBLIC_SOURCE_LABELS[source.id]) return;

      var status = clean(source.status, 40);
      var statusLabel =
        status === 'live'
          ? publicWords('可用', '可用', 'Available')
          : status === 'unavailable'
            ? publicWords('暫時無法載入', '暂时无法加载', 'Unavailable')
            : publicWords('狀態待核實', '状态待核实', 'Status unknown');

      var li = document.createElement('li');

      li.setAttribute('data-source-status', status);

      li.textContent = publicSourceName(source.id) + ' · ' + statusLabel;

      host.appendChild(li);
    });

    host.hidden = host.childElementCount === 0;
  }

  function renderPublicDetail(root, row, message) {
    var selection = q(root, '[data-kid-selected-public-place]');
    if (!selection || !row) return;

    var name = q(selection, '[data-kid-selected-public-place-name]');
    var meta = q(selection, '[data-kid-selected-public-place-meta]');
    var source = q(selection, '[data-kid-selected-public-place-source]');
    var status = q(selection, '[data-kid-selected-public-place-status]');
    var link = q(selection, '[data-kid-selected-public-place-link]');

    if (name) name.textContent = publicPlaceName(row);
    if (meta) meta.textContent = publicPlaceMeta(row);

    if (source) {
      source.textContent =
        publicWords('官方來源：', '官方来源：', 'Official source: ') +
        publicSourceName(row.source && row.source.id);
    }

    if (status) status.textContent = message || '';

    if (link) {
      var href = publicOfficialUrl(row.source && row.source.official_url);

      link.hidden = !href;

      if (href) link.href = href;
      else link.removeAttribute('href');
    }

    selection.hidden = false;
  }

  async function loadPublicPlaceDetail(root, row) {
    var url = publicCoreUrl(true);

    if (!url) {
      renderPublicDetail(
        root,
        row,
        publicWords(
          '官方詳情端點未設定。',
          '官方详情接口未设置。',
          'Official detail endpoint is unavailable.'
        )
      );
      return;
    }

    var requestedId = clean((row.source && row.source.upstream_place_id) || row.id, 160);

    url.searchParams.set('place_id', requestedId);

    var sequence = root.__kidPublicSequence;
    var token = root.__kidPublicDetailToken;
    const controller = new AbortController();

    root.__kidPublicDetailAbort = controller;

    try {
      var response = await window.fetch(url.toString(), {
        method: 'GET',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      var body = await response.json();

      if (
        sequence !== root.__kidPublicSequence ||
        token !== root.__kidPublicDetailToken ||
        root.__kidPublicSelectedId !== row.id
      ) {
        return;
      }

      var detail = body && body.data && (body.data.item || body.data.place);

      if (
        !response.ok ||
        body.success !== true ||
        !detail ||
        detail.id !== row.id ||
        !detail.source ||
        detail.source.id !== row.source.id ||
        (requestedId !== row.id && detail.source.upstream_place_id !== requestedId)
      ) {
        throw new Error('public_place_identity_mismatch');
      }

      renderPublicDetail(
        root,
        detail,
        publicWords(
          '已讀取官方地點詳情。',
          '已读取官方地点详情。',
          'Official place details loaded.'
        )
      );
    } catch (error) {
      if (
        controller.signal.aborted ||
        sequence !== root.__kidPublicSequence ||
        token !== root.__kidPublicDetailToken
      ) {
        return;
      }

      renderPublicDetail(
        root,
        row,
        publicWords(
          '暫時無法讀取最新詳情，請查看官方公告。',
          '暂时无法读取最新详情，请查看官方来源。',
          'Latest details are unavailable. Check the official source.'
        )
      );
    } finally {
      if (root.__kidPublicDetailAbort === controller) {
        root.__kidPublicDetailAbort = null;
      }
    }
  }

  function selectPublicPlace(root, id, fromPin) {
    var row = root.__kidPublicPlaces && root.__kidPublicPlaces[id];

    if (!row) return false;

    if (root.__kidPublicSelectedId === id) {
      clearPublicSelection(root, false);
      return true;
    }

    clearPublicSelection(root, false);

    root.__kidPublicSelectedId = id;

    qa(root, '[data-kid-public-card-id]').forEach(function (button) {
      var selected = button.getAttribute('data-kid-public-card-id') === id;
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.setAttribute('aria-expanded', selected ? 'true' : 'false');
    });

    var renderer = window.LittleGKidMapRenderer;

    if (renderer && typeof renderer.setPublicExploreSelection === 'function') {
      renderer.setPublicExploreSelection(root, id);
    }

    renderPublicDetail(
      root,
      row,
      publicWords('正在讀取官方詳情…', '正在读取官方详情…', 'Loading official details…')
    );

    setMapView(root, row.location.latitude, row.location.longitude, 14);

    if (fromPin) {
      var selectedCard = qa(root, '[data-kid-public-card-id]').find(function (button) {
        return button.getAttribute('data-kid-public-card-id') === id;
      });

      if (selectedCard) {
        selectedCard.scrollIntoView({
          block: 'nearest',
        });
        selectedCard.focus({ preventScroll: true });
      }
    }

    if (root.__kidPublicSourceGroup === 'learn') {
      renderPublicDetail(
        root,
        row,
        publicWords(
          '已使用官方學校／圖書館位置資料。',
          '已使用官方学校／图书馆位置数据。',
          'Official school/library location data loaded.'
        )
      );
    } else {
      loadPublicPlaceDetail(root, row);
    }
    return true;
  }

  function renderPublicCards(root, rows) {
    var host = q(root, '[data-kid-public-places-results]');
    if (!host) return;

    host.replaceChildren();

    var fragment = document.createDocumentFragment();

    rows.forEach(function (row) {
      var li = document.createElement('li');
      var button = document.createElement('button');

      button.type = 'button';
      button.className = 'kid-map-public-places__card';

      button.setAttribute('data-kid-public-place-card', '');
      button.setAttribute('data-kid-public-card-id', row.id);
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-expanded', 'false');

      var selection = q(root, '[data-kid-selected-public-place]');
      if (selection && selection.id) {
        button.setAttribute('aria-controls', selection.id);
      }

      var name = document.createElement('strong');
      name.textContent = publicPlaceName(row);

      var meta = document.createElement('small');
      meta.textContent = publicPlaceMeta(row);

      var source = document.createElement('small');

      source.textContent =
        publicWords('官方來源：', '官方来源：', 'Official source: ') +
        publicSourceName(row.source.id);

      button.appendChild(name);
      button.appendChild(meta);
      button.appendChild(source);

      button.addEventListener('click', function () {
        selectPublicPlace(root, row.id, false);
      });

      li.appendChild(button);
      fragment.appendChild(li);
    });

    host.appendChild(fragment);
  }

  function renderPublicPins(root, rows) {
    var renderer = window.LittleGKidMapRenderer;

    if (!renderer || typeof renderer.setNearbyPoints !== 'function') {
      return false;
    }

    var rendered = renderer.setNearbyPoints(root, {
      type: PUBLIC_EXPLORE_TYPE,

      points: rows.map(function (row) {
        return {
          id: row.id,
          lat: row.location.latitude,
          lng: row.location.longitude,
          label: publicPlaceName(row),
        };
      }),

      onSelect: function (id) {
        selectPublicPlace(root, id, true);
      },
    });

    if (
      rendered &&
      root.__kidPublicSelectedId &&
      typeof renderer.setPublicExploreSelection === 'function'
    ) {
      renderer.setPublicExploreSelection(root, root.__kidPublicSelectedId);
    }

    return rendered;
  }

  function ensurePublicPinsReady(root, sequence, attempt) {
    if (
      !root ||
      !root.isConnected ||
      root.__kidPublicSequence !== sequence ||
      !Array.isArray(root.__kidPublicRows) ||
      !root.__kidPublicRows.length
    ) {
      return;
    }

    var panel = q(root, '[data-kid-public-places]');

    if (renderPublicPins(root, root.__kidPublicRows)) {
      if (panel) {
        panel.setAttribute('data-kid-public-pins-state', 'ready');
      }
      return;
    }

    if (attempt >= 80) {
      if (panel) {
        panel.setAttribute('data-kid-public-pins-state', 'unavailable');
      }

      var status = q(root, '[data-kid-public-places-status]');

      if (status) {
        status.textContent +=
          ' ' +
          publicWords(
            '地點清單可用，但地圖標記暫時無法顯示。',
            '地点清单可用，但地图标记暂时无法显示。',
            'The list is available, but map pins could not be displayed.'
          );
      }
      return;
    }

    if (panel) {
      panel.setAttribute('data-kid-public-pins-state', 'pending');
    }

    root.__kidPublicPinsTimer = window.setTimeout(function () {
      root.__kidPublicPinsTimer = 0;
      ensurePublicPinsReady(root, sequence, attempt + 1);
    }, 150);
  }

  function currentPublicExploreCenter(root) {
    var renderer = window.LittleGKidMapRenderer;
    var map = renderer && typeof renderer.mapFor === 'function' ? renderer.mapFor(root) : null;
    var center = map && typeof map.getCenter === 'function' ? map.getCenter() : null;
    var latitude = finite(center && center.lat);
    var longitude = finite(center && center.lng);

    return validCoordinates(latitude, longitude) ? { lat: latitude, lng: longitude } : null;
  }

  function publicGroupLoadingMessage(sourceGroup) {
    return sourceGroup === 'learn'
      ? publicWords(
          '正在載入附近的官方學校與公共圖書館…',
          '正在加载附近的官方学校与公共图书馆…',
          'Loading official schools and public libraries nearby…'
        )
      : publicWords(
          '正在載入康文署公共遊樂地點…',
          '正在加载康文署公共游乐地点…',
          'Loading official LCSD play places…'
        );
  }

  async function loadPublicPlaces(root, sourceGroup) {
    if (!PUBLIC_GROUP_SOURCES[sourceGroup]) return false;

    root.__kidPublicSourceGroup = sourceGroup;
    clearPublicExplore(root, true);

    var sequence = root.__kidPublicSequence;
    const controller = new AbortController();

    root.__kidPublicListAbort = controller;

    var url = publicCoreUrl(false);

    setPublicPlacesState(root, 'loading', publicGroupLoadingMessage(sourceGroup), sourceGroup);

    if (!url) {
      setPublicPlacesState(
        root,
        'error',
        publicWords(
          '公共資料端點未設定。',
          '公共数据接口未设置。',
          'Public Explore endpoint is unavailable.'
        ),
        sourceGroup
      );
      return false;
    }

    url.searchParams.set('source_group', sourceGroup);
    url.searchParams.set('limit', '100');

    if (sourceGroup === 'learn') {
      var center = currentPublicExploreCenter(root);

      if (!center) {
        setPublicPlacesState(
          root,
          'error',
          publicWords(
            '地圖位置尚未準備好，請稍後再試。',
            '地图位置尚未准备好，请稍后再试。',
            'The map location is not ready yet. Try again shortly.'
          ),
          sourceGroup
        );
        return false;
      }

      url.searchParams.set('lat', String(center.lat));
      url.searchParams.set('lng', String(center.lng));
      url.searchParams.set('radius_m', '5000');
    }

    try {
      var response = await window.fetch(url.toString(), {
        method: 'GET',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      var body = await response.json();

      if (sequence !== root.__kidPublicSequence) return false;

      if (
        !response.ok ||
        !body ||
        body.success !== true ||
        !body.data ||
        !Array.isArray(body.data.items) ||
        !Array.isArray(body.data.source_breakdown)
      ) {
        throw new Error('public_explore_unavailable');
      }

      var sources = body.data.source_breakdown;
      var expectedSources = PUBLIC_GROUP_SOURCES[sourceGroup];

      if (
        sources.length !== expectedSources.length ||
        !expectedSources.every(function (id) {
          return sources.some(function (source) {
            return source && source.id === id;
          });
        })
      ) {
        throw new Error('public_source_contract_mismatch');
      }

      var places = Object.create(null);
      var rows = [];

      body.data.items.forEach(function (item) {
        var row = normalizePublicPlace(item, sourceGroup);

        if (!row || places[row.id]) return;

        places[row.id] = row;
        rows.push(row);
      });

      if (body.data.items.length && !rows.length) {
        throw new Error('public_results_invalid');
      }

      root.__kidPublicPlaces = places;
      root.__kidPublicRows = rows;

      renderPublicSources(root, sources);
      renderPublicCards(root, rows);
      ensurePublicPinsReady(root, sequence, 0);

      var count = q(root, '[data-kid-public-places-count]');
      var empty = q(root, '[data-kid-public-places-empty]');

      var filteredTotal = finite(body.data.filtered_total);

      if (count) {
        count.hidden = false;
        count.textContent =
          filteredTotal !== null && filteredTotal > rows.length
            ? rows.length + ' / ' + filteredTotal
            : String(rows.length);
      }

      if (empty) empty.hidden = rows.length > 0;

      var partial =
        (body.data.freshness && body.data.freshness.status === 'partial') ||
        sources.some(function (source) {
          return source.status !== 'live';
        }) ||
        (Array.isArray(body.data.source_errors) && body.data.source_errors.length > 0);

      var message = rows.length
        ? sourceGroup === 'learn'
          ? publicWords(
              '已載入 ' + rows.length + ' 個附近學習地點。',
              '已加载 ' + rows.length + ' 个附近学习地点。',
              rows.length + ' learning places loaded nearby.'
            )
          : publicWords(
              '已載入 ' + rows.length + ' 個公共地點。',
              '已加载 ' + rows.length + ' 个公共地点。',
              rows.length + ' public places loaded.'
            )
        : sourceGroup === 'learn'
          ? publicWords(
              '附近暫時沒有符合條件的學校或公共圖書館。',
              '附近暂时没有符合条件的学校或公共图书馆。',
              'No matching schools or public libraries were found nearby.'
            )
          : publicWords(
              '目前沒有符合條件的公共地點。',
              '目前没有符合条件的公共地点。',
              'No matching public places were found.'
            );

      if (partial) {
        message +=
          ' ' +
          publicWords(
            '部分官方來源暫時無法載入，結果可能不完整。',
            '部分官方来源暂时无法加载，结果可能不完整。',
            'Some official sources are unavailable. Results may be incomplete.'
          );
      }

      setPublicPlacesState(root, rows.length ? 'ready' : 'empty', message, sourceGroup);

      return true;
    } catch (error) {
      if (controller.signal.aborted || sequence !== root.__kidPublicSequence) {
        return false;
      }

      setPublicPlacesState(
        root,
        'error',
        sourceGroup === 'learn'
          ? publicWords(
              '官方學習地點暫時無法載入，請重試「學習」。',
              '官方学习地点暂时无法加载，请重试“学习”。',
              'Official learning places are unavailable. Try Learn again.'
            )
          : publicWords(
              '官方公共資料暫時無法載入，請重試 Play。',
              '官方公共数据暂时无法加载，请重试 Play。',
              'Official public data is unavailable. Try Play again.'
            ),
        sourceGroup
      );

      return false;
    } finally {
      if (root.__kidPublicListAbort === controller) {
        root.__kidPublicListAbort = null;
      }
    }
  }

  function clickNearbyProxy(root, type) {
    var proxy = q(root, '[data-gproperty-map-nearby-type="' + type + '"]');
    if (!proxy) return false;
    proxy.click();
    return true;
  }

  function clearNearbyProxies(root) {
    qa(root, '[data-gproperty-map-nearby-type][aria-pressed="true"]').forEach(function (button) {
      button.click();
    });
  }

  function bindPublicSelection(root) {
    var close = q(root, '[data-kid-public-selection-close]');
    if (!close || close.__kidPublicSelectionBound) return;
    close.__kidPublicSelectionBound = true;

    close.addEventListener('click', function () {
      clearPublicSelection(root, true);
    });
  }

  function bindNearbyContext(root) {
    var details = q(root, '[data-kid-nearby-details]');
    if (!details || details.__kidNearbyBound) return;

    details.__kidNearbyBound = true;

    function resetClosedNearby() {
      if (details.open) return;

      clearPublicExplore(root);

      var kind = root.getAttribute('data-kid-active-filter') || 'all';

      if (['play', 'learn', 'attractions', 'treats'].indexOf(kind) >= 0) {
        clearNearbyProxies(root);
        root.setAttribute('data-kid-active-filter', 'all');

        qa(root, '[data-kid-filter]').forEach(function (button) {
          setPressed(button, false);
        });

        renderSocialPins(root);
      }
    }

    details.addEventListener('toggle', resetClosedNearby);

    root.addEventListener('littleg:kid-map:nearby-close', resetClosedNearby);
  }

  function bindClusterDismissal(root) {
    if (!root || root.__kidClusterDismissBound) return;
    root.__kidClusterDismissBound = true;

    root.addEventListener('gproperty:map:blank-click', function () {
      setResultsExplicit(root, false);
      if (!root.__kidExpandedStationId) return;
      root.__kidExpandedStationId = '';
      renderSocialPins(root);
    });
  }

  function bindFilters(root) {
    qa(root, '[data-kid-filter]').forEach(function (button) {
      button.addEventListener('click', function () {
        var kind = button.getAttribute('data-kid-filter') || 'all';
        qa(root, '[data-kid-filter]').forEach(function (item) {
          setPressed(item, item === button);
        });
        root.setAttribute('data-kid-active-filter', kind);
        closeFriendCard(root);

        var publicSourceGroup = clean(button.getAttribute('data-kid-public-source-group'), 20);

        if (!publicSourceGroup) {
          clearPublicExplore(root);
        }

        if (publicSourceGroup) {
          clearNearbyProxies(root);
          renderSocialPins(root);
          loadPublicPlaces(root, publicSourceGroup);
          return;
        }

        if (kind === 'all' || kind === 'friends' || kind === 'mine') {
          clearNearbyProxies(root);
          renderSocialPins(root);
          if ((kind === 'friends' || kind === 'mine') && !visibleSocialRows(root).length) {
            toast(
              root,
              kind === 'friends'
                ? isChinese()
                  ? '還沒有朋友。按「新增」開始。'
                  : 'No friends yet. Tap Add to start.'
                : isChinese()
                  ? '還沒有地點。按「新增」選一個港鐵站。'
                  : 'No places yet. Tap Add and choose a station.'
            );
          }
          return;
        }

        renderSocialPins(root);
        var nearbyType = button.getAttribute('data-kid-nearby-proxy');
        if (nearbyType) {
          clearNearbyProxies(root);
          if (!clickNearbyProxy(root, nearbyType))
            toast(root, isChinese() ? '這一層暫時未有資料。' : 'No data here yet.');
        } else if (button.hasAttribute('data-kid-source-required')) {
          clearNearbyProxies(root);
          toast(root, isChinese() ? '這一層暫時未有資料。' : 'No data here yet.');
        }
      });
    });
  }

  function socialSearchMatches(query) {
    var needle = normalizeSearchValue(query);
    if (!needle) return [];
    return socialRows()
      .filter(function (row) {
        var value = normalizeSearchValue(row.label);
        return value === needle || value.indexOf(needle) === 0 || value.indexOf(needle) >= 0;
      })
      .slice(0, 5);
  }

  function focusSocialSearchResult(root, row) {
    return row ? openSocialPlace(root, row.id) : false;
  }

  function injectLocalSuggestions(root) {
    var slot = q(root, '[data-kid-map-search-slot]');
    var searchRoot = slot
      ? q(slot, '[data-kid-map-search-root], [data-gproperty-map-search-root]')
      : null;
    var input = searchRoot ? q(searchRoot, '[data-gproperty-map-search-input]') : null;
    var list = searchRoot ? q(searchRoot, '[data-gproperty-map-suggestions]') : null;
    if (!input || !list) return;

    if (root.__kidSearchSuggestionsSettled === true) {
      list.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-activedescendant', '');
      return;
    }

    var query = input.value;
    var hasQuery = !!normalizeSearchValue(query);
    var explicitStations = root.__kidExplicitStationSearch === true;
    var stations = hasQuery || explicitStations ? searchStations(query).slice(0, 5) : [];
    var friends = socialSearchMatches(query).slice(0, 3);

    qa(list, '[data-kid-local-search-result]').forEach(function (node) {
      node.remove();
    });
    qa(list, '[data-gproperty-map-history-term]').forEach(function (node) {
      var row = node.closest('li');
      if (row) row.remove();
    });
    if (!hasQuery && !explicitStations) {
      list.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-activedescendant', '');
      return;
    }
    var fragment = document.createDocumentFragment();

    stations.forEach(function (station) {
      var li = document.createElement('li');
      li.setAttribute('data-kid-local-search-result', 'station');
      li.setAttribute('role', 'option');
      li.className = 'kid-station-result';

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'kid-station-result__button';
      button.setAttribute('data-kid-global-station-result', station.stationId);
      if (station.lineColor)
        button.style.setProperty('--kid-station-line-color', station.lineColor);

      button.innerHTML =
        '<span class="kid-station-result__badge" aria-hidden="true"></span><span class="kid-station-result__copy"><strong></strong><small></small></span>';
      appendStationMakiIcon(root, button.querySelector('.kid-station-result__badge'));
      button.querySelector('strong').textContent = stationDisplayName(station);
      button.querySelector('small').textContent = station.lineIds.map(lineLabel).join(' · ');
      li.appendChild(button);
      fragment.appendChild(li);
    });

    friends.forEach(function (row) {
      var li = document.createElement('li');
      li.setAttribute('data-kid-local-search-result', row.kind === 'mine' ? 'place' : 'friend');
      li.setAttribute('role', 'option');

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'kid-friend-search-result';
      button.setAttribute('data-kid-global-friend-result', row.id);

      var label =
        clean(row.label, 80) ||
        (row.kind === 'mine'
          ? isChinese()
            ? '我的地點'
            : 'My place'
          : isChinese()
            ? '朋友'
            : 'Friend');
      var initial = Array.from(label)[0] || '•';
      var station =
        row.locationType === 'station_anchor'
          ? clean(
              isChinese()
                ? row.stationNameZhHk || row.stationName
                : row.stationName || row.stationNameZhHk,
              120
            )
          : '';

      button.innerHTML =
        '<span class="kid-friend-search-result__avatar" aria-hidden="true"></span><span class="kid-friend-search-result__copy"><strong></strong><small></small></span>';
      button.querySelector('.kid-friend-search-result__avatar').textContent =
        row.kind === 'mine' ? '🏠' : initial.toUpperCase();
      button.querySelector('strong').textContent = label;
      button.querySelector('small').textContent =
        station || (isChinese() ? '私人好友地圖' : 'Private friends map');

      li.appendChild(button);
      fragment.appendChild(li);
    });

    if (fragment.childNodes.length) list.insertBefore(fragment, list.firstChild);
    var localOptions = qa(list, '[data-kid-local-search-result]');
    localOptions.forEach(function (option, index) {
      option.id = searchRoot.id + '-kid-local-' + index;
      option.setAttribute('aria-selected', 'false');
    });
    if (localOptions.length) {
      searchRoot.__suggestionIndex = -1;
      input.setAttribute('aria-activedescendant', '');
    }
    list.hidden = list.childElementCount === 0;
    input.setAttribute('aria-expanded', list.hidden ? 'false' : 'true');
  }

  function setResultsExplicit(root, value) {
    if (!(root instanceof HTMLElement)) return;
    if (value === null) {
      root.removeAttribute('data-kid-results-explicit');
      return;
    }
    root.setAttribute('data-kid-results-explicit', value ? 'true' : 'false');
  }

  function bindMergedSearch(root) {
    var slot = q(root, '[data-kid-map-search-slot]');
    var searchRoot = slot
      ? q(slot, '[data-kid-map-search-root], [data-gproperty-map-search-root]')
      : null;
    var form = searchRoot ? q(searchRoot, '[data-gproperty-map-search-form]') : null;
    var input = searchRoot ? q(searchRoot, '[data-gproperty-map-search-input]') : null;
    var list = searchRoot ? q(searchRoot, '[data-gproperty-map-suggestions]') : null;
    if (!form || !input || !list || form.__kidMergedSearchBound) return;
    form.__kidMergedSearchBound = true;

    var injectTimer = 0;
    var observer = null;

    function observeSuggestions() {
      if (observer) observer.observe(list, { childList: true });
    }

    function scheduleInject() {
      window.clearTimeout(injectTimer);

      if (root.__kidSearchSuggestionsSettled === true) {
        injectLocalSuggestions(root);
        return;
      }

      injectTimer = window.setTimeout(function () {
        injectTimer = 0;
        if (observer) observer.disconnect();
        try {
          injectLocalSuggestions(root);
        } finally {
          observeSuggestions();
        }
      }, 40);
    }

    input.addEventListener(
      'keydown',
      function (event) {
        if (
          list.hidden ||
          (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter')
        )
          return;
        var localOptions = qa(list, '[data-kid-local-search-result]');
        if (!localOptions.length) return;
        var options = qa(
          list,
          '[data-kid-local-search-result], [data-gproperty-map-suggestion-index]'
        );
        var activeId = input.getAttribute('aria-activedescendant');
        var activeIndex = options.findIndex(function (option) {
          return option.id === activeId;
        });
        if (event.key === 'Enter' && activeIndex < 0) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (event.key === 'Enter') {
          var selected = options[activeIndex];
          var target = selected.matches('[data-kid-local-search-result]')
            ? q(selected, 'button')
            : selected;
          if (target) target.click();
          return;
        }

        var nextIndex =
          event.key === 'ArrowDown'
            ? (activeIndex + 1) % options.length
            : activeIndex < 0
              ? options.length - 1
              : (activeIndex + options.length - 1) % options.length;
        options.forEach(function (option, index) {
          var selected = index === nextIndex;
          option.classList.toggle('is-active', selected);
          option.setAttribute('aria-selected', selected ? 'true' : 'false');
        });
        searchRoot.__suggestionIndex = -1;
        input.setAttribute('aria-activedescendant', options[nextIndex].id);
        if (typeof options[nextIndex].scrollIntoView === 'function') {
          options[nextIndex].scrollIntoView({ block: 'nearest' });
        }
      },
      true
    );

    input.addEventListener('focus', function () {
      root.__kidSearchSuggestionsSettled = false;
      setResultsExplicit(root, false);
      if (!normalizeSearchValue(input.value) && root.__kidExplicitStationSearch !== true) {
        list.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-activedescendant', '');
      }
    });

    input.addEventListener('blur', function () {
      if (!input.value.trim()) {
        window.setTimeout(function () {
          if (document.activeElement !== input && !list.contains(document.activeElement)) {
            root.__kidExplicitStationSearch = false;
            setResultsExplicit(root, null);
          }
        }, 150);
      }
    });

    input.addEventListener('input', function () {
      root.__kidSearchSuggestionsSettled = false;
      var hasQuery = !!normalizeSearchValue(input.value);
      if (hasQuery) root.__kidExplicitStationSearch = false;
      if (!hasQuery && root.__kidExplicitStationSearch !== true) {
        list.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-activedescendant', '');
      }
      scheduleInject();
    });

    list.addEventListener(
      'click',
      function (event) {
        var stationButton = event.target.closest('[data-kid-global-station-result]');
        if (stationButton) {
          event.preventDefault();
          event.stopImmediatePropagation();
          var station = stationById(stationButton.getAttribute('data-kid-global-station-result'));
          if (station) {
            root.__kidExplicitStationSearch = false;
            root.__kidSearchSuggestionsSettled = true;
            setMapView(root, station.lat, station.lng, 15);
            input.value = stationDisplayName(station);
            list.hidden = true;
            input.setAttribute('aria-expanded', 'false');
            input.setAttribute('aria-activedescendant', '');
            toast(root, '🚉 ' + stationDisplayName(station));
          }
          return;
        }

        var friendButton = event.target.closest('[data-kid-global-friend-result]');
        if (friendButton) {
          event.preventDefault();
          event.stopImmediatePropagation();
          var row = annotationById(friendButton.getAttribute('data-kid-global-friend-result'));
          if (!row) return;

          input.value = clean(row.label, 80);
          root.__kidSearchSuggestionsSettled = true;
          list.hidden = true;
          input.setAttribute('aria-expanded', 'false');
          input.setAttribute('aria-activedescendant', '');
          focusSocialSearchResult(root, row);
        }
      },
      true
    );

    form.addEventListener(
      'submit',
      function (event) {
        var query = normalizeSearchValue(input.value);
        if (!query) {
          setResultsExplicit(root, false);
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        var friend = socialSearchMatches(input.value)[0] || null;
        var station = searchStations(input.value)[0] || null;
        if (station && stationRank(station, query) <= 1) {
          setResultsExplicit(root, false);
          event.preventDefault();
          event.stopImmediatePropagation();
          setMapView(root, station.lat, station.lng, 15);
          toast(root, '🚉 ' + stationDisplayName(station));
          return;
        }
        if (friend) {
          setResultsExplicit(root, false);
          event.preventDefault();
          event.stopImmediatePropagation();
          focusSocialSearchResult(root, friend);
          return;
        }
        setResultsExplicit(root, true);
      },
      true
    );

    window.addEventListener('gproperty:estate:selected', function () {
      setResultsExplicit(root, false);
    });

    observer = new MutationObserver(scheduleInject);
    observeSuggestions();
  }

  function showGlobalStationSearch(root) {
    var slot = q(root, '[data-kid-map-search-slot]');
    var searchRoot = slot
      ? q(slot, '[data-kid-map-search-root], [data-gproperty-map-search-root]')
      : null;
    var input = searchRoot ? q(searchRoot, '[data-gproperty-map-search-input]') : null;
    var list = searchRoot ? q(searchRoot, '[data-gproperty-map-suggestions]') : null;
    if (!root || !input || !list) return Promise.resolve(false);

    return loadStationDataset()
      .then(function () {
        root.__kidSearchSuggestionsSettled = false;
        root.__kidExplicitStationSearch = true;
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
        injectLocalSuggestions(root);
        list.hidden = list.childElementCount === 0;
        input.setAttribute('aria-expanded', list.hidden ? 'false' : 'true');
        return !list.hidden;
      })
      .catch(function () {
        toast(
          root,
          isChinese() ? '車站資料暫時未能載入。' : 'Station data is temporarily unavailable.'
        );
        return false;
      });
  }

  function mountSearchDock(root) {
    var slot = q(root, '[data-kid-map-search-slot]');
    var searchRoot = document.querySelector('[data-gproperty-map-search-root]');
    if (!slot || !searchRoot) return;

    if (searchRoot.parentNode !== slot) slot.appendChild(searchRoot);
    searchRoot.setAttribute('data-kid-map-search', 'true');

    var form = q(searchRoot, '[data-gproperty-map-search-form]');
    var combobox = q(searchRoot, '.gproperty-map-search__combobox');
    var input = q(searchRoot, '[data-gproperty-map-search-input]');
    var submit = q(searchRoot, '[data-gproperty-map-search-submit]');

    if (!form || !combobox || !input || !submit || q(combobox, '[data-kid-voice-search]')) return;

    var voice = document.createElement('button');
    voice.type = 'button';
    voice.className = 'kid-map__voice-search';
    voice.setAttribute('data-kid-voice-search', '');
    voice.setAttribute('aria-pressed', 'false');
    voice.setAttribute('aria-label', isChinese() ? '語音搜尋' : 'Voice search');
    voice.innerHTML = '<span aria-hidden="true">🎙</span>';

    combobox.insertBefore(voice, submit);

    var Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    var recognition = null;

    voice.addEventListener('click', function () {
      if (!Recognition) {
        toast(root, isChinese() ? '可使用鍵盤語音輸入。' : 'Use keyboard dictation instead.');
        input.focus();
        return;
      }
      if (recognition) {
        try {
          recognition.stop();
        } catch (_) {}
        return;
      }

      recognition = new Recognition();
      recognition.lang = isChinese() ? 'zh-HK' : 'en-HK';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onstart = function () {
        voice.classList.add('is-listening');
        voice.setAttribute('aria-pressed', 'true');
        voice.removeAttribute('data-kid-voice-error');
      };

      recognition.onresult = function (event) {
        var transcript = clean(
          event &&
            event.results &&
            event.results[0] &&
            event.results[0][0] &&
            event.results[0][0].transcript,
          160
        );
        if (transcript) {
          input.value = transcript;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      };

      recognition.onerror = function (event) {
        var code = clean(event && event.error, 40) || 'unknown';
        voice.setAttribute('data-kid-voice-error', code);
        var denied = code === 'not-allowed' || code === 'service-not-allowed';
        var noSpeech = code === 'no-speech';
        var message = denied
          ? isChinese()
            ? '麥克風權限未開啟，請改用鍵盤搜尋。'
            : 'Microphone access is off. Use text search instead.'
          : noSpeech
            ? isChinese()
              ? '沒有聽到語音，請再試一次或改用鍵盤。'
              : 'No speech detected. Try again or use text search.'
            : isChinese()
              ? '語音搜尋暫時未能使用，請改用鍵盤。'
              : 'Voice search is temporarily unavailable. Use text search instead.';
        toast(root, message);
        input.focus();
      };

      recognition.onend = function () {
        voice.classList.remove('is-listening');
        voice.setAttribute('aria-pressed', 'false');
        recognition = null;
      };

      try {
        recognition.start();
      } catch (_) {
        recognition = null;
        voice.setAttribute('data-kid-voice-error', 'start-failed');
        toast(
          root,
          isChinese()
            ? '語音搜尋暫時未能使用，請改用鍵盤。'
            : 'Voice search is temporarily unavailable. Use text search instead.'
        );
        input.focus();
      }
    });
  }

  function bindZoom(root) {
    var zoomIn = q(root, '[data-kid-zoom-in]');
    var zoomOut = q(root, '[data-kid-zoom-out]');

    if (zoomIn) {
      zoomIn.addEventListener('click', function () {
        var renderer = window.LittleGKidMapRenderer;
        if (renderer && typeof renderer.zoomIn === 'function') renderer.zoomIn(root);
      });
    }

    if (zoomOut) {
      zoomOut.addEventListener('click', function () {
        var renderer = window.LittleGKidMapRenderer;
        if (renderer && typeof renderer.zoomOut === 'function') renderer.zoomOut(root);
      });
    }
  }

  // No independent parent-approval workflow. Existing pending records remain fail-closed.

  function selectedPlace(root) {
    var runtime =
      root && root.__selectedPlace && typeof root.__selectedPlace === 'object'
        ? root.__selectedPlace
        : null;
    var name = clean(
      (runtime && (runtime.name_zh || runtime.name_en || runtime.name)) ||
        text(q(root, '[data-gproperty-map-selected-name]')),
      180
    );
    var address = clean(
      (runtime && (runtime.address || runtime.address_zh || runtime.formatted_address_zh)) ||
        text(q(root, '[data-gproperty-map-selected-address]')),
      260
    );

    if (!name || name === 'A place' || name === '一個地方') return null;
    return {
      name: name,
      address: address,
      key: clean((runtime && runtime.key) || (name + '|' + address).toLocaleLowerCase(), 220),
    };
  }

  function syncStampProjection(root) {
    var count = q(root, '[data-kid-stamp-count]');
    var items = readLocal(STAMP_KEY);

    if (count) count.textContent = String(items.length);
    projectFamilyStats(root);
  }

  function bindStamp(root) {
    var button = q(root, '[data-kid-stamp-place]');
    if (!button) return;

    button.addEventListener('click', function () {
      var place = selectedPlace(root);
      if (!place) return;

      var items = readLocal(STAMP_KEY);
      var index = items.findIndex(function (item) {
        return item && item.key === place.key;
      });

      if (index >= 0) items.splice(index, 1);
      else
        items.unshift({
          key: place.key,
          name: place.name,
          address: place.address,
          stampedAt: new Date().toISOString(),
        });

      writeLocal(STAMP_KEY, items);
      syncStampProjection(root);
      setPressed(button, index < 0);
    });
  }

  function bindSave(root) {
    var button = q(root, '[data-kid-save-place]');
    if (!button) return;

    button.addEventListener('click', function () {
      var place = selectedPlace(root);
      if (!place) return;

      var items = readLocal(SAVE_KEY);
      var index = items.findIndex(function (item) {
        return item && item.key === place.key;
      });

      if (index >= 0) {
        items.splice(index, 1);
        setPressed(button, false);
      } else {
        items.unshift({
          key: place.key,
          name: place.name,
          address: place.address,
          savedAt: new Date().toISOString(),
        });
        setPressed(button, true);
      }

      writeLocal(SAVE_KEY, items);
    });
  }

  function bindMascot(root) {
    var button = q(root, '[data-kid-mascot]');
    var bubble = q(root, '[data-kid-mascot-bubble]');
    var message = q(root, '[data-kid-mascot-text]');

    if (!button || !bubble || !message) return;

    button.addEventListener('click', function () {
      var opening = bubble.hidden;
      bubble.hidden = !opening;
      button.setAttribute('aria-expanded', opening ? 'true' : 'false');
      if (opening) {
        message.textContent = socialRows().length
          ? isChinese()
            ? '點朋友看看附近，或按新增選一個港鐵站。'
            : 'Tap a friend to look nearby, or add someone with a station anchor.'
          : isChinese()
            ? '按「新增」，再選一個附近港鐵站。'
            : 'Tap Add, then choose a nearby MTR station.';
      }
    });
  }

  function bindSocialGraphRefresh(root) {
    if (root.__kidSocialRefreshBound) return;
    root.__kidSocialRefreshBound = true;

    document.addEventListener('littleg:family-social-graph-change', function () {
      renderSocialPins(root);
      var openId = clean(root.__kidOpenSocialId, 120);
      if (openId && !annotationById(openId)) closeFriendCard(root);
    });
  }

  /* 優先級 Escape 按鍵調度 (Priority Escape Dismissal) */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    var stationModal = document.querySelector(
      '.littleg-kid-map [data-kid-station-modal].is-open, .littleg-kid-map [data-kid-station-modal][open]'
    );
    if (stationModal) {
      event.stopPropagation();
      var stationRoot = stationModal.closest('[data-littleg-kid-map]');
      if (stationRoot) closeStationPicker(stationRoot);
      return;
    }

    var addModal = document.querySelector('.littleg-kid-map [data-kid-map-add-modal][open]');
    if (addModal) {
      event.stopPropagation();
      setModalOpen(addModal, false);
      cleanWorkflowRoute();
      return;
    }

    var friendCard = document.querySelector(
      '.littleg-kid-map [data-kid-friend-card]:not([hidden])'
    );
    if (friendCard) {
      event.stopPropagation();
      var cardRoot = friendCard.closest('[data-littleg-kid-map]');
      if (cardRoot) closeFriendCard(cardRoot);
    }
  });

  function mountHeaderGeometry(root) {
    if (!root || root.__kidHeaderGeometryCleanup) return;

    const bar = q(root, '[data-kid-map-local-bar]');
    if (!bar) return;

    let frame = 0;
    let disposed = false;

    const measure = () => {
      frame = 0;
      if (disposed || !root.isConnected) return;

      const rootRect = root.getBoundingClientRect();
      const barRect = bar.getBoundingClientRect();
      if (!rootRect.width || !barRect.height) return;

      const top = Math.ceil(Math.max(0, barRect.bottom - rootRect.top) + 12);
      const value = `${top}px`;

      if (root.style.getPropertyValue('--kid-header-panel-top') !== value) {
        root.style.setProperty('--kid-header-panel-top', value);
      }
    };

    const schedule = () => {
      if (disposed || frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    const observer = window.ResizeObserver ? new window.ResizeObserver(schedule) : null;

    if (observer) {
      observer.observe(bar);
      observer.observe(root);
    }

    window.addEventListener('resize', schedule);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', schedule);
    }

    root.__kidHeaderGeometryCleanup = () => {
      disposed = true;
      if (observer) observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);

      window.removeEventListener('resize', schedule);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', schedule);
      }

      root.style.removeProperty('--kid-header-panel-top');
      root.__kidHeaderGeometryCleanup = null;
    };

    schedule();
  }

  function mount(root) {
    if (!root || root.__kidMapMounted === VERSION) return;
    root.__kidMapMounted = VERSION;

    mountSearchDock(root);
    mountHeaderGeometry(root);
    bindMergedSearch(root);
    bindStationPicker(root);
    bindFilters(root);
    bindPublicSelection(root);
    bindNearbyContext(root);
    bindClusterDismissal(root);
    bindZoom(root);
    bindSocialActions(root);
    bindMemoryViewProjection(root);
    bindStamp(root);
    bindSave(root);
    bindMascot(root);
    bindSocialGraphRefresh(root);

    syncStampProjection(root);
    waitForSocialRenderer(root, 0);

    loadStationDataset()
      .then(function () {
        root.setAttribute('data-kid-stations-ready', 'true');
        var input = q(root, '[data-kid-map-search-input]');
        if (
          input &&
          (normalizeSearchValue(input.value) || root.__kidExplicitStationSearch === true)
        ) {
          injectLocalSuggestions(root);
        }
      })
      .catch(function () {
        root.setAttribute('data-kid-stations-ready', 'false');
      });

    // data-kid-map-ready is set only by the Mapbox renderer after style/load.
  }

  function mountAll() {
    qa(document, ROOT).forEach(mount);
  }

  function publicOpenSocialPlace(id, options) {
    var root = document.querySelector(ROOT);
    return root ? openSocialPlace(root, id, options || {}) : false;
  }

  function publicOpenAdd(kind) {
    var root = document.querySelector(ROOT);
    if (!root) return false;

    openAnnotationModal(root, {
      kind: kind === 'mine' ? 'mine' : 'friend',
    });

    var modal = q(root, '[data-kid-map-add-modal]');
    return Boolean(modal && modal.open);
  }

  // Shopify Section Unload 生命週期釋放與解綁 (Memory Leak Prevention)
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target ? event.target.querySelector(ROOT) : null;
    if (!(root instanceof HTMLElement)) return;

    if (root.__kidHeaderGeometryCleanup) {
      root.__kidHeaderGeometryCleanup();
    }

    if (root.__kidIdentityObserver) root.__kidIdentityObserver.disconnect();
    if (root.__kidActiveStateObserver) root.__kidActiveStateObserver.disconnect();
    if (root.__kidPartnerObserver) root.__kidPartnerObserver.disconnect();
    if (root.__kidPublicPoiHandoffObserver) root.__kidPublicPoiHandoffObserver.disconnect();

    window.clearTimeout(root.__kidNewFriendTimer);
    window.clearTimeout(root.__kidDangerTimer);
    if (
      root.__kidSocialZoomMap &&
      root.__kidSocialZoomHandler &&
      typeof root.__kidSocialZoomMap.off === 'function'
    ) {
      root.__kidSocialZoomMap.off('zoomend', root.__kidSocialZoomHandler);
      root.__kidSocialZoomMap.off('resize', root.__kidSocialZoomHandler);
    }
    root.__kidSocialZoomMap = null;
    root.__kidSocialZoomHandler = null;

    if (root.__kidFriendCardResizeObserver) {
      root.__kidFriendCardResizeObserver.disconnect();
      root.__kidFriendCardResizeObserver = null;
    }

    if (root.__kidFriendPaddingResizeHandler) {
      window.removeEventListener('resize', root.__kidFriendPaddingResizeHandler);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', root.__kidFriendPaddingResizeHandler);
      }

      root.__kidFriendPaddingResizeHandler = null;
    }

    if (root.__kidFriendPaddingFrame) {
      window.cancelAnimationFrame(root.__kidFriendPaddingFrame);
      root.__kidFriendPaddingFrame = 0;
    }

    if (
      root.__kidFriendPaddingMap &&
      root.__kidFriendPaddingBase &&
      typeof root.__kidFriendPaddingMap.setPadding === 'function'
    ) {
      try {
        root.__kidFriendPaddingMap.setPadding(root.__kidFriendPaddingBase);
      } catch (_) {}
    }

    root.__kidFriendPaddingMap = null;
    root.__kidFriendPaddingBase = null;

    root.__kidMapMounted = null;
    lastActiveFocusElement = null;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', mountAll);

  window.LittleGKidMap = Object.freeze({
    mount: mountAll,
    notify: function (message, host) {
      var root = document.querySelector(ROOT);
      if (!root) return false;
      return toast(root, message, host);
    },
    openSocialPlace: publicOpenSocialPlace,
    syncFriendCardPadding: function () {
      var root = document.querySelector(ROOT);
      return root ? syncFriendCardPadding(root) : false;
    },
    openAdd: publicOpenAdd,
    loadStationDataset: loadStationDataset,
    searchStations: searchStations,
    showGlobalStations: function () {
      var root = document.querySelector(ROOT);
      return root ? showGlobalStationSearch(root) : Promise.resolve(false);
    },
    openStationPicker: function () {
      var root = document.querySelector(ROOT);
      if (root) openStationPicker(root);
    },
    closeStationPicker: function () {
      var root = document.querySelector(ROOT);
      if (root) closeStationPicker(root);
    },
    selectStation: function (stationId) {
      var root = document.querySelector(ROOT);
      var station = stationById(stationId);
      return !!root && !!station && selectStation(root, station);
    },
    saveFriendWithStationAnchor: function () {
      var root = document.querySelector(ROOT);
      var form = root && q(root, '[data-kid-map-add-form]');
      return !!root && !!form && saveFriendWithStationAnchor(root, form);
    },
    renderStationAnchoredMarker: renderStationAnchoredMarker,
    version: VERSION,
    storage: Object.freeze({
      stamps: STAMP_KEY,
      saved: SAVE_KEY,
      socialOwner: 'LittleGFamilyData.socialGraph',
    }),
  });
})(window, document);
